import { useState, useEffect, useCallback } from "react";
import { useProcessos, useCobrancas, useFluxo, popularDadosIniciais, uploadArquivo } from "./useFirestore";
import { auth, googleProvider, usersRef, db, doc, setDoc, getDoc, updateDoc as fbUpdateUserDoc, onSnapshot as fbUserSnapshot, getDocs, query, collection, storage, ref as storageRef, deleteObject, signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, signOut, onAuthStateChanged } from "./firebase";
import { sendPasswordResetEmail } from "firebase/auth";

// ── Dados agora vêm do Firestore (banco de dados na nuvem) ──

// ── Helpers ──────────────────────────────────────────────────────────────
const fmt = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
const statusColor = (s) => ({
  "DEFESA PRÉVIA": "#38bdf8",              // azul — 1ª instância
  "INDEFERIDO DEFESA PRÉVIA": "#fb923c",   // laranja — perdeu a DP (segue p/ JARI)
  "RECURSO JARI": "#f59e0b",               // âmbar — 2ª instância
  "INDEFERIDO JARI": "#f43f5e",            // rosa — perdeu a JARI (segue p/ CETRAN)
  "RECURSO CETRAN": "#8b5cf6",             // roxo — 3ª instância
  "INDEFERIDO CETRAN": "#dc2626",          // vermelho — perdeu o CETRAN
  "DEFERIDO": "#22c55e",                   // verde — ganhou
  "INDEFERIDO": "#ef4444",                 // vermelho — perdeu (genérico)
  "CANCELADO": "#78716c",                  // cinza — encerrado
}[s] || "#22d3ee");
const prescricaoColor = (d) => d <= 0 ? "#78716c" : d <= 30 ? "#ef4444" : d <= 90 ? "#f59e0b" : "#22c55e";
const payStatusColor = (s) => ({ pago: "#22c55e", pendente: "#f59e0b", atrasado: "#ef4444" }[s] || "#78716c");
const statusAbrev = (s) => ({ "DEFESA PRÉVIA": "DP", "INDEFERIDO DEFESA PRÉVIA": "Ind.DP", "RECURSO JARI": "JARI", "INDEFERIDO JARI": "Ind.JARI", "RECURSO CETRAN": "CETRAN", "INDEFERIDO CETRAN": "Ind.CETRAN", "DEFERIDO": "Def.", "INDEFERIDO": "Indef.", "CANCELADO": "Canc." }[s] || (s || "").slice(0, 4));

// ── Permissões por perfil (espelham as regras do Firestore) ──
// verFinanceiro: vê aba Financeiro/Cobranças e valores. editProcesso: cria/edita
// processos e sobe documentos. editFinanceiro: registra pagamentos, retiradas,
// baixa cobranças. gerirUsuarios: aprova usuários e vê Configurações.
const PERMISSOES = {
  admin:        ["verFinanceiro", "editProcesso", "editFinanceiro", "gerirUsuarios"],
  processos:    ["editProcesso"],
  financeiro:   ["verFinanceiro", "editFinanceiro"],
  visualizador: [],
};
const can = (role, acao) => (PERMISSOES[role] || []).includes(acao);

// Detecta telas estreitas (celular/tablet) — sincroniza com matchMedia.
function useIsMobile(maxWidth = 820) {
  const q = `(max-width: ${maxWidth}px)`;
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mql = window.matchMedia(q);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener ? mql.addEventListener("change", onChange) : mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => { mql.removeEventListener ? mql.removeEventListener("change", onChange) : mql.removeListener(onChange); };
  }, [q]);
  return isMobile;
}

// Validação de arquivo no cliente (espelha as regras do Storage: 10MB, PDF/imagem).
const MAX_UPLOAD_MB = 10;
const arquivoInvalido = (file) => {
  if (!file) return null;
  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) return `Arquivo maior que ${MAX_UPLOAD_MB}MB`;
  if (!/^(application\/pdf|image\/)/.test(file.type || "")) return "Envie um PDF ou imagem";
  return null;
};

// ── Documentos: mapa canônico ÚNICO (evita divergência entre telas/uploads) ──
// Aceita as duas grafias já gravadas em dados legados ("JARI" e "Recurso JARI").
const DOC_KEYS = [
  { key: "cnh", labels: ["CNH"] },
  { key: "defesaPrevia", labels: ["Defesa Prévia"] },
  { key: "jari", labels: ["JARI", "Recurso JARI"] },
  { key: "cetran", labels: ["CETRAN", "Recurso CETRAN"] },
];
const canonDocKey = (tipo) => (DOC_KEYS.find(d => d.labels.includes(tipo)) || {}).key || "";
// Fonte ÚNICA de verdade do status de documentos = o array `arquivos` (não os flags).
const docStatus = (p) => {
  const arqs = (p && p.arquivos) || [];
  const has = (key) => arqs.some(a => a.url && canonDocKey(a.tipo) === key);
  return { cnh: has("cnh"), defesaPrevia: has("defesaPrevia"), jari: has("jari"), cetran: has("cetran") };
};

// Prazo prescricional (em dias), contado da data da infração, conforme TIPO e FASE:
//  • LS em Defesa Prévia .......... 360 dias
//  • LS fora da defesa prévia ..... 5 anos (1.825 dias)  [RECURSO JARI / CETRAN]
//  • Cassação / Suspensão ......... 3 anos (1.095 dias), independente da fase
const prazoPrescricaoDias = (tipo, status) => {
  if (tipo === "LS") return status === "DEFESA PRÉVIA" ? 360 : 1825;
  return 1095;
};
const STATUS_ENCERRADO = ["CANCELADO", "DEFERIDO", "INDEFERIDO"];
// Recalcula a prescrição a partir da data da infração — NUNCA usa o valor
// salvo (que congela no dia do cadastro). Processos encerrados = 0.
const calcDiasPrescricao = (p) => {
  if (!p || STATUS_ENCERRADO.includes(p.status) || !p.dtInfracao) return 0;
  const dtInf = new Date(p.dtInfracao + "T12:00:00");
  if (isNaN(dtInf.getTime())) return typeof p.diasPrescricao === "number" ? p.diasPrescricao : 0;
  return Math.max(0, Math.ceil((dtInf.getTime() + prazoPrescricaoDias(p.tipo, p.status) * 864e5 - Date.now()) / 864e5));
};

// ── Shared Components ───────────────────────────────────────────────────
const GS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,700;0,9..40,800;1,9..40,400&family=JetBrains+Mono:wght@400;600&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; font-variant-numeric: tabular-nums; }
html, body, #root { max-width: 100vw; overflow-x: hidden; }
::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes glow { 0%,100% { box-shadow: 0 0 20px rgba(34,211,238,0.15); } 50% { box-shadow: 0 0 40px rgba(34,211,238,0.3); } }
input, select, button, textarea { font-family: inherit; }
/* Feedback de hover (estilos inline não fazem :hover) */
button:not(:disabled) { transition: filter .15s ease, background .15s ease, transform .05s ease; }
button:not(:disabled):hover { filter: brightness(1.14); }
button:not(:disabled):active { transform: translateY(1px); }
/* Foco visível por teclado (acessibilidade) */
button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible { outline: 2px solid #22d3ee; outline-offset: 2px; }
/* Números alinhados (evita "pulo" das casas) */
input, select { accent-color: #22d3ee; }
`;

function KPI({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.06))", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "18px 18px", position: "relative", overflow: "hidden", minWidth: 0 }}>
      <div style={{ position: "absolute", top: -8, right: -8, fontSize: 56, opacity: 0.06, pointerEvents: "none" }}>{icon}</div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
      {/* Valor adaptável: encolhe conforme a largura do cartão para nunca ser cortado. */}
      <div style={{ fontSize: "clamp(19px, 2.1vw, 26px)", fontWeight: 800, color: color || "#fff", letterSpacing: -0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
    </div>
  );
}

function Pill({ text, color }) {
  return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: color + "18", color, border: `1px solid ${color}33`, whiteSpace: "nowrap" }}>{text}</span>;
}

function Btn({ children, onClick, variant = "primary", style = {}, disabled }) {
  const base = { border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", transition: "all 0.2s", fontFamily: "inherit", opacity: disabled ? 0.5 : 1, ...style };
  const styles = { primary: { ...base, background: "linear-gradient(135deg, #22d3ee, #0891b2)", color: "#fff" }, secondary: { ...base, background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }, danger: { ...base, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" } };
  return <button onClick={onClick} style={styles[variant]} disabled={disabled}>{children}</button>;
}

function Input({ label, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>{label}</label>}
      <input {...props} style={{ padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14, outline: "none", transition: "border 0.2s", ...(props.style || {}) }} />
    </div>
  );
}

function Card({ children, style = {}, modal }) {
  const bg = modal ? { background: "#151c2e", border: "1px solid rgba(255,255,255,0.12)" } : { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" };
  return <div style={{ ...bg, borderRadius: 16, padding: 24, ...style }}>{children}</div>;
}

function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: active ? "rgba(34,211,238,0.1)" : "transparent", border: active ? "1px solid rgba(34,211,238,0.2)" : "1px solid transparent", color: active ? "#22d3ee" : "rgba(255,255,255,0.45)", cursor: "pointer", fontWeight: active ? 700 : 500, fontSize: 13, width: "100%", transition: "all 0.2s", fontFamily: "inherit", position: "relative" }}>
      <span style={{ fontSize: 18 }}>{icon}</span> {label}
      {badge > 0 && <span style={{ marginLeft: "auto", background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 10, minWidth: 18, textAlign: "center" }}>{badge}</span>}
    </button>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  const clr = toast.type === "err" ? "#ef4444" : toast.type === "warn" ? "#f59e0b" : "#22c55e";
  const icon = toast.type === "err" ? "✕" : toast.type === "warn" ? "!" : "✓";
  return (
    <div style={{ position: "fixed", bottom: 24, right: 16, left: 16, marginLeft: "auto", zIndex: 300, display: "flex", alignItems: "center", gap: 10, padding: "13px 18px", borderRadius: 12, background: "#151c2e", border: `1px solid ${clr}55`, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", animation: "slideIn 0.3s ease", width: "fit-content", maxWidth: "min(400px, calc(100vw - 32px))" }}>
      <div style={{ width: 24, height: 24, borderRadius: "50%", background: clr + "22", color: clr, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{icon}</div>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{toast.msg}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | register | forgot

  // Salva/atualiza perfil do user no Firestore
  const saveUserProfile = async (firebaseUser) => {
    const userDoc = doc(usersRef, firebaseUser.uid);
    const snap = await getDoc(userDoc);
    if (snap.exists()) {
      // User já existe — retorna dados do Firestore
      return { uid: firebaseUser.uid, email: firebaseUser.email, ...snap.data() };
    } else {
      // Primeiro login — cria perfil (pendente até admin aprovar)
      const profile = {
        email: firebaseUser.email,
        nome: firebaseUser.displayName || firebaseUser.email.split("@")[0],
        empresa: empresa.trim(),
        role: "pendente",
        criadoEm: new Date().toISOString()
      };
      await setDoc(userDoc, profile);
      return { uid: firebaseUser.uid, ...profile };
    }
  };

  const handleSubmit = async () => {
    if (mode === "forgot") {
      if (!email) { setError("Digite seu e-mail"); return; }
      setLoading(true); setError("");
      try {
        await sendPasswordResetEmail(auth, email);
        setError(""); setMode("login");
        alert("E-mail de recuperação enviado! Verifique sua caixa de entrada.");
      } catch (err) {
        setError(err.code === "auth/user-not-found" ? "E-mail não cadastrado" : "Erro ao enviar e-mail");
      }
      setLoading(false);
      return;
    }
    if (!email || !pass) { setError("Preencha todos os campos"); return; }
    if (pass.length < 6) { setError("Senha deve ter no mínimo 6 caracteres"); return; }
    setLoading(true); setError("");
    try {
      let result;
      if (mode === "register") {
        result = await createUserWithEmailAndPassword(auth, email, pass);
      } else {
        result = await signInWithEmailAndPassword(auth, email, pass);
      }
      const profile = await saveUserProfile(result.user);
      onLogin(profile);
    } catch (err) {
      const msgs = {
        "auth/user-not-found": "E-mail não cadastrado",
        "auth/wrong-password": "Senha incorreta",
        "auth/invalid-credential": "E-mail ou senha incorretos",
        "auth/email-already-in-use": "E-mail já cadastrado. Faça login.",
        "auth/weak-password": "Senha muito fraca (mínimo 6 caracteres)",
        "auth/invalid-email": "E-mail inválido",
        "auth/too-many-requests": "Muitas tentativas. Aguarde e tente novamente."
      };
      setError(msgs[err.code] || "Erro: " + err.message);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setLoading(true); setError("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const profile = await saveUserProfile(result.user);
      onLogin(profile);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError("Erro ao entrar com Google");
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{GS}</style>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse at 30% 20%, rgba(34,211,238,0.06) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(139,92,246,0.04) 0%, transparent 60%)" }} />

      <div style={{ position: "relative", width: "min(420px, 92vw)", animation: "fadeIn 0.6s ease" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: -1 }}>
            <span style={{ color: "#22d3ee" }}>Lex</span><span style={{ color: "#fff" }}>Driver</span>
          </div>
          <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginTop: 4 }}>Recursos de Trânsito</div>
        </div>

        {/* Card */}
        <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "36px 32px", backdropFilter: "blur(20px)", animation: "glow 3s infinite" }}>

          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
            {mode === "login" ? "Entrar" : mode === "register" ? "Criar conta" : "Recuperar senha"}
          </h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", marginBottom: 28 }}>
            {mode === "login" ? "Acesse sua conta LexDriver" : mode === "register" ? "Crie sua conta para começar" : "Enviaremos um link de recuperação"}
          </p>

          <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Input label="Email" type="email" placeholder="seu@email.com" value={email} onChange={e => setEmail(e.target.value)} />

            {mode !== "forgot" && (
              <Input label="Senha" type="password" placeholder="••••••••" value={pass} onChange={e => setPass(e.target.value)} />
            )}

            {mode === "register" && (
              <Input label="Nome da Empresa" type="text" placeholder="Ex: LexDriver Recursos" value={empresa} onChange={e => setEmpresa(e.target.value)} />
            )}

            {error && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#ef4444" }}>{error}</div>}

            <button type="submit" disabled={loading} style={{
              width: "100%", padding: "14px", borderRadius: 12, border: "none",
              background: loading ? "rgba(34,211,238,0.3)" : "linear-gradient(135deg, #22d3ee, #0891b2)",
              color: "#fff", fontSize: 15, fontWeight: 800, cursor: loading ? "wait" : "pointer",
              transition: "all 0.2s", fontFamily: "inherit", marginTop: 4,
            }}>
              {loading ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                  Verificando...
                </span>
              ) : mode === "login" ? "Entrar" : mode === "register" ? "Criar conta" : "Enviar link"}
            </button>

            {/* Google login */}
            <button type="button" onClick={handleGoogle} disabled={loading} style={{
              width: "100%", padding: "12px", borderRadius: 12,
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: "inherit",
              opacity: loading ? 0.6 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Entrar com Google
            </button>
          </form>

          {/* Links */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            {mode === "login" && (
              <>
                <button onClick={() => setMode("register")} style={{ background: "none", border: "none", color: "#22d3ee", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Criar conta</button>
                <button onClick={() => setMode("forgot")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Esqueci a senha</button>
              </>
            )}
            {mode !== "login" && (
              <button onClick={() => setMode("login")} style={{ background: "none", border: "none", color: "#22d3ee", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Voltar ao login</button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 32, fontSize: 11, color: "rgba(255,255,255,0.2)" }}>
          LexDriver Recursos · Autenticação via Firebase Auth
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN APP (authenticated)
// ══════════════════════════════════════════════════════════════════════════
function MainApp({ user, onLogout }) {
  const [page, setPage] = useState("dashboard");
  // Perfil financeiro decidido antes das assinaturas: quem não vê financeiro
  // nem assina cobranças/caixa (alinha UI com as regras do Firestore).
  const podeFinanceiro = can(user.role, "verFinanceiro");
  // ── Dados vêm do Firestore (tempo real) ──
  const { processos: rawProcessos, loading, addProcesso: fbAddProcesso, updateProcesso: fbUpdateProcesso } = useProcessos();
  // diasPrescricao é sempre derivado da data da infração (o valor salvo no
  // banco fica desatualizado). Recalculado a cada render — lista pequena.
  const processos = rawProcessos.map(p => ({ ...p, diasPrescricao: calcDiasPrescricao(p) }));
  const { cobrancas, addCobranca, updateCobranca } = useCobrancas(podeFinanceiro);
  const { fluxo, addMovimentacao } = useFluxo(podeFinanceiro);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("TODOS");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [showNewCob, setShowNewCob] = useState(false);
  const [showNewProcesso, setShowNewProcesso] = useState(false);
  const [showEditProcesso, setShowEditProcesso] = useState(null);
  const [showNewFinanceiro, setShowNewFinanceiro] = useState(false);
  const [novoProc, setNovoProc] = useState({ ait: "", cliente: "", telefone: "", dtInfracao: "", dtRecurso: "", tipo: "LS", honorario: "", formaPgto: "A VISTA", status: "DEFESA PRÉVIA" });
  const [novaEntrada, setNovaEntrada] = useState({ descricao: "", valor: "", tipo: "entrada", data: new Date().toISOString().split("T")[0] });
  const [periodoFin, setPeriodoFin] = useState("tudo");
  const [showFinPanel, setShowFinPanel] = useState(false);
  const [parcelaValor, setParcelaValor] = useState("");
  const [consultaOk, setConsultaOk] = useState(false);
  const [importando, setImportando] = useState(false);
  const [novoProcCnh, setNovoProcCnh] = useState(null);
  const [novoProcRecibo, setNovoProcRecibo] = useState(null);
  const [showStatusUpload, setShowStatusUpload] = useState(false);
  const [showRetirada, setShowRetirada] = useState(false);
  const [retirada, setRetirada] = useState({ socio: "", valor: "", data: new Date().toISOString().split("T")[0], obs: "" });
  const [retiradaComprovante, setRetiradaComprovante] = useState(null);

  const [statusUploadFile, setStatusUploadFile] = useState(null);
  const [pendingStatus, setPendingStatus] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [showRoleSelect, setShowRoleSelect] = useState(null);
  const [novaCob, setNovaCob] = useState({ processoId: "", valor: "", metodo: "PIX", data: new Date().toISOString().split("T")[0], parcela: "1/1" });

  // ── Permissões do usuário logado ──
  const canEditProc = can(user.role, "editProcesso");
  const canEditFin = can(user.role, "editFinanceiro");
  const canVerFin = can(user.role, "verFinanceiro");
  const canGerirUsers = can(user.role, "gerirUsuarios");

  // ── Toast (avisos de sucesso/erro) ──
  const [toast, setToast] = useState(null);
  const notify = useCallback((msg, type = "ok") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); }, []);
  const isMobile = useIsMobile();
  const [mobileNav, setMobileNav] = useState(false);

  // Fecha qualquer modal aberto com a tecla Esc.
  const fecharModais = useCallback(() => {
    setShowUpload(false); setShowNewCob(false); setShowNewProcesso(false);
    setShowEditProcesso(null); setShowNewFinanceiro(false); setShowStatusUpload(false);
    setShowRetirada(false); setShowRoleSelect(null);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") fecharModais(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fecharModais]);

  // Listener de usuários do Firestore (tempo real)
  useEffect(() => {
    const unsub = fbUserSnapshot(usersRef, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      setAllUsers(list);
    }, (err) => console.error("Erro ao carregar usuários:", err));
    return () => unsub();
  }, []);

  const approveUser = async (uid, role) => {
    try {
      await fbUpdateUserDoc(doc(db, "users", uid), { role });
      setShowRoleSelect(null);
      notify("Usuário aprovado como " + role);
    } catch { notify("Erro ao aprovar usuário", "err"); }
  };
  const rejectUser = async (uid) => {
    try {
      await fbUpdateUserDoc(doc(db, "users", uid), { role: "rejeitado" });
      notify("Usuário rejeitado", "warn");
    } catch { notify("Erro ao rejeitar usuário", "err"); }
  };

  const addProcesso = async () => {
    if (!canEditProc) return notify("Sem permissão para cadastrar processos", "err");
    if (!novoProc.ait || !novoProc.cliente) return notify("Preencha AIT e cliente", "warn");
    if (!novoProc.dtInfracao) return notify("Informe a data da infração", "warn");
    setImportando(true);
    try {
      const dias = calcDiasPrescricao({ status: novoProc.status, dtInfracao: novoProc.dtInfracao, tipo: novoProc.tipo });
      const reciboKey = novoProc.status === "RECURSO JARI" ? "jari" : novoProc.status === "RECURSO CETRAN" ? "cetran" : "defesaPrevia";
      const honor = parseFloat(novoProc.honorario) || 0;

      // 1) Cria o processo primeiro para obter o ID REAL do documento...
      const novoId = await fbAddProcesso({
        ait: novoProc.ait, cliente: novoProc.cliente, telefone: novoProc.telefone || "",
        dtInfracao: novoProc.dtInfracao, dtRecurso: novoProc.dtRecurso || "",
        dtConsulta: new Date().toISOString().split("T")[0],
        tipo: novoProc.tipo, diasPrescricao: dias,
        status: novoProc.status, honorario: honor,
        formaPgto: novoProc.formaPgto, saldoPagar: honor,
        cnh: "Pendente", defesaPrevia: "Pendente", jari: "Pendente", cetran: "Pendente",
        arquivos: [], pagamentos: []
      });

      // 2) ...e só então sobe os arquivos sob a pasta do ID real (evita órfãos/pasta fantasma).
      const arquivos = [];
      const flags = {};
      if (novoProcCnh) { arquivos.push(await uploadArquivo(novoId, "CNH", novoProcCnh)); flags.cnh = "doc"; }
      if (novoProcRecibo) {
        const tipoRecibo = novoProc.status === "RECURSO JARI" ? "JARI" : novoProc.status === "RECURSO CETRAN" ? "CETRAN" : "Defesa Prévia";
        arquivos.push(await uploadArquivo(novoId, tipoRecibo, novoProcRecibo));
        flags[reciboKey] = "doc";
      }
      if (arquivos.length) await fbUpdateProcesso(novoId, { arquivos, ...flags });

      setNovoProc({ ait: "", cliente: "", telefone: "", dtInfracao: "", dtRecurso: "", tipo: "LS", honorario: "", formaPgto: "A VISTA", status: "DEFESA PRÉVIA" });
      setNovoProcCnh(null); setNovoProcRecibo(null);
      setShowNewProcesso(false);
      notify("Processo cadastrado com sucesso");
    } catch (e) {
      notify("Erro ao cadastrar processo: " + (e?.message || "tente novamente"), "err");
    }
    setImportando(false);
  };

  const updateProcesso = async (id, changes) => {
    await fbUpdateProcesso(id, changes);
    if (selectedProcess?.id === id) setSelectedProcess(prev => ({ ...prev, ...changes }));
  };


  const registrarParcela = async (id, valor, obs) => {
    if (!canEditFin) return notify("Sem permissão para registrar pagamentos", "err");
    if (importando) return; // trava de duplo-clique
    if (!valor || valor <= 0) return notify("Informe um valor válido", "warn");
    const hoje = new Date().toISOString().split("T")[0];
    const proc = processos.find(p => p.id === id);
    if (!proc) return;
    if (proc.saldoPagar <= 0) return notify("Honorário já está quitado", "warn");
    // Nunca credita mais que o saldo devedor — evita receita fantasma no caixa.
    const aplicado = Math.min(valor, proc.saldoPagar);
    setImportando(true);
    try {
      const novosPag = [...(proc.pagamentos || []), { valor: aplicado, data: hoje, obs: obs || "Pagamento registrado" }];
      const novoSaldo = Math.max(0, proc.saldoPagar - aplicado);
      await fbUpdateProcesso(id, { saldoPagar: novoSaldo, pagamentos: novosPag });
      if (selectedProcess?.id === id) setSelectedProcess(prev => ({ ...prev, saldoPagar: novoSaldo, pagamentos: novosPag }));
      // Todo pagamento vira uma ENTRADA no livro caixa (fonte única do caixa).
      await addMovimentacao({
        descricao: "Honorário — " + proc.cliente + (obs ? " (" + obs + ")" : ""),
        valor: aplicado, tipo: "entrada", categoria: "honorario",
        refProcesso: id, data: hoje, registradoPor: user.email,
      });
      notify("Pagamento de " + fmt(aplicado) + " registrado");
    } catch (e) {
      notify("Erro ao registrar pagamento", "err");
    }
    setImportando(false);
    setParcelaValor("");
    setShowFinPanel(false);
  };

  const estornarPagamento = async (processoId, pgIndex) => {
    if (!canEditFin) return notify("Sem permissão para estornar", "err");
    const motivo = window.prompt("Motivo do estorno:");
    if (!motivo) return;
    const proc = processos.find(p => p.id === processoId);
    if (!proc) return;
    const pags = [...(proc.pagamentos || [])];
    const pg = pags[pgIndex];
    if (!pg || pg.estornado) return;
    try {
      // Marca como estornado (não deleta — trilha de auditoria)
      pags[pgIndex] = { ...pg, estornado: true, motivoEstorno: motivo, dataEstorno: new Date().toISOString().split("T")[0], estornadoPor: user.email };
      // Devolve o valor ao saldo, mas nunca acima do honorário (evita "JÁ PAGO" negativo).
      const novoSaldo = Math.min(proc.honorario, proc.saldoPagar + pg.valor);
      await fbUpdateProcesso(processoId, { saldoPagar: novoSaldo, pagamentos: pags });
      if (selectedProcess?.id === processoId) setSelectedProcess(prev => ({ ...prev, saldoPagar: novoSaldo, pagamentos: pags }));
      // Reverte no livro caixa: estorno de honorário = SAÍDA (mantém o caixa correto).
      await addMovimentacao({
        descricao: "[ESTORNO] Honorário — " + proc.cliente + " — Motivo: " + motivo,
        valor: pg.valor, tipo: "saida", categoria: "estorno_honorario",
        refProcesso: processoId, data: new Date().toISOString().split("T")[0], registradoPor: user.email,
      });
      notify("Pagamento estornado", "warn");
    } catch { notify("Erro ao estornar", "err"); }
  };

  // ── Cobranças ──
  const criarCobranca = async () => {
    if (!canEditFin) return notify("Sem permissão para criar cobranças", "err");
    if (importando) return;
    const proc = processos.find(p => p.id === novaCob.processoId);
    const valor = parseFloat(novaCob.valor) || 0;
    if (!proc) return notify("Selecione o cliente/processo", "warn");
    if (valor <= 0) return notify("Informe um valor válido", "warn");
    setImportando(true);
    try {
      await addCobranca({
        processoId: proc.id, cliente: proc.cliente, valor,
        metodo: novaCob.metodo, status: "pendente",
        data: novaCob.data, parcela: novaCob.parcela || "1/1",
      });
      setNovaCob({ processoId: "", valor: "", metodo: "PIX", data: new Date().toISOString().split("T")[0], parcela: "1/1" });
      setShowNewCob(false);
      notify("Cobrança criada");
    } catch { notify("Erro ao criar cobrança", "err"); }
    setImportando(false);
  };

  const baixarCobranca = async (c) => {
    if (!canEditFin) return notify("Sem permissão para baixar cobranças", "err");
    if (importando) return; // trava de duplo-clique
    const hoje = new Date().toISOString().split("T")[0];
    try {
      await updateCobranca(c.id, { status: "pago", pagoEm: hoje });
      if (c.processoId && processos.some(p => p.id === c.processoId)) {
        // Ligada a um processo: abate o saldo E lança no caixa (via registrarParcela).
        await registrarParcela(c.processoId, c.valor, "Cobrança " + (c.metodo || ""));
      } else {
        // Sem processo vinculado (ex.: cobranças-semente): ainda assim entra no caixa.
        await addMovimentacao({
          descricao: "Cobrança recebida — " + (c.cliente || "cliente") + (c.metodo ? " (" + c.metodo + ")" : ""),
          valor: c.valor || 0, tipo: "entrada", categoria: "cobranca",
          refCobranca: c.id, data: hoje, registradoPor: user.email,
        });
        notify("Cobrança baixada e lançada no caixa");
      }
    } catch { notify("Erro ao baixar cobrança", "err"); }
  };

  const addRetiradaSocio = async () => {
    if (!canEditFin) return notify("Sem permissão para registrar retiradas", "err");
    if (!retirada.valor || !retirada.socio) return notify("Informe o sócio e o valor", "warn");
    setImportando(true);
    try {
      let comprovanteUrl = "";
      if (retiradaComprovante) {
        const result = await uploadArquivo("retiradas", "comprovante_" + Date.now(), retiradaComprovante);
        comprovanteUrl = result.url || "";
      }
      await addMovimentacao({
        descricao: "Retirada sócio: " + retirada.socio + (retirada.obs ? " — " + retirada.obs : ""),
        valor: parseFloat(retirada.valor) || 0,
        tipo: "saida",
        categoria: "retirada_socio",
        socio: retirada.socio,
        data: retirada.data,
        comprovanteUrl,
        registradoPor: user.email,
      });
      setRetirada({ socio: "", valor: "", data: new Date().toISOString().split("T")[0], obs: "" });
      setRetiradaComprovante(null);
      setShowRetirada(false);
      notify("Retirada registrada");
    } catch (e) { console.error("Retirada falhou:", e); notify("Erro na retirada: " + (e?.code || e?.message || "desconhecido"), "err"); }
    setImportando(false);
  };

  const estornarMovimentacao = async (movId) => {
    if (!canEditFin) return notify("Sem permissão para estornar", "err");
    // Movimentações do livro caixa são documentos planos em fluxo_caixa.
    const mov = fluxo.find(m => m.id === movId);
    if (!mov) return notify("Movimentação não encontrada", "err");
    const motivo = window.prompt("Motivo do estorno:");
    if (!motivo) return;
    try {
      await addMovimentacao({
        descricao: "[ESTORNO] " + (mov.descricao || "Movimentação") + " — Motivo: " + motivo,
        valor: mov.valor || 0,
        tipo: mov.tipo === "entrada" ? "saida" : "entrada",
        categoria: "estorno",
        refOriginal: movId,
        data: new Date().toISOString().split("T")[0],
        registradoPor: user.email,
      });
      notify("Movimentação estornada", "warn");
    } catch { notify("Erro ao estornar", "err"); }
  };

  const addFinanceiro = async () => {
    if (!canEditFin) return notify("Sem permissão para lançar no caixa", "err");
    if (!novaEntrada.valor) return notify("Informe o valor", "warn");
    try {
      await addMovimentacao({
        descricao: novaEntrada.descricao || (novaEntrada.tipo === "entrada" ? "Entrada avulsa" : "Saída avulsa"),
        valor: parseFloat(novaEntrada.valor) || 0,
        tipo: novaEntrada.tipo,
        data: novaEntrada.data,
        registradoPor: user.email,
      });
      setNovaEntrada({ descricao: "", valor: "", tipo: "entrada", data: new Date().toISOString().split("T")[0] });
      setShowNewFinanceiro(false);
      notify("Movimentação registrada");
    } catch { notify("Erro ao registrar movimentação", "err"); }
  };

  const handleImport = async () => {
    if (!canEditProc) return notify("Sem permissão para importar dados", "err");
    setImportando(true);
    try {
      const ok = await popularDadosIniciais();
      notify(ok ? "Dados importados com sucesso" : "O banco já tinha dados");
    } catch { notify("Erro ao importar dados", "err"); }
    setImportando(false);
  };

  const handleStatusChange = (newStatus) => {
    setPendingStatus(newStatus);
    setStatusUploadFile(null);
    setShowStatusUpload(true);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus || !selectedProcess) return;
    if (!canEditProc) return notify("Sem permissão para alterar processos", "err");
    setImportando(true);
    try {
      const docKey = pendingStatus === "RECURSO JARI" ? "jari" : pendingStatus === "RECURSO CETRAN" ? "cetran" : pendingStatus === "DEFESA PRÉVIA" ? "defesaPrevia" : null;
      const changes = { status: pendingStatus };

      if (statusUploadFile && docKey) {
        const tipoRecibo = pendingStatus === "RECURSO JARI" ? "JARI" : pendingStatus === "RECURSO CETRAN" ? "CETRAN" : "Defesa Prévia";
        const result = await uploadArquivo(selectedProcess.id, tipoRecibo, statusUploadFile);
        changes[docKey] = "doc";
        changes.arquivos = [...(selectedProcess.arquivos || []), result];
      }

      await fbUpdateProcesso(selectedProcess.id, changes);
      setSelectedProcess(prev => ({ ...prev, ...changes }));
      setShowStatusUpload(false);
      setPendingStatus("");
      setStatusUploadFile(null);
      notify("Status alterado para " + pendingStatus);
    } catch (e) { console.error("Alterar status falhou:", e); notify("Erro ao alterar status: " + (e?.code || e?.message || "desconhecido"), "err"); }
    setImportando(false);
  };

  // Edição completa dos dados do processo (modal Editar).
  const salvarEdicaoProcesso = async () => {
    if (!canEditProc || !showEditProcesso) return notify("Sem permissão", "err");
    const e = showEditProcesso;
    if (!e.ait || !e.cliente) return notify("AIT e cliente são obrigatórios", "warn");
    try {
      const changes = {
        ait: e.ait, cliente: e.cliente, telefone: e.telefone || "",
        dtInfracao: e.dtInfracao || "", dtRecurso: e.dtRecurso || "",
        tipo: e.tipo, formaPgto: e.formaPgto,
      };
      await fbUpdateProcesso(e.id, changes);
      if (selectedProcess?.id === e.id) setSelectedProcess(prev => ({ ...prev, ...changes }));
      setShowEditProcesso(null);
      notify("Processo atualizado");
    } catch { notify("Erro ao salvar processo", "err"); }
  };

  const ativos = processos.filter(p => p.status !== "CANCELADO");
  const totalHon = ativos.reduce((s, p) => s + p.honorario, 0);
  const totalReceber = ativos.reduce((s, p) => s + p.saldoPagar, 0);
  const alertasCrit = ativos.filter(p => p.diasPrescricao > 0 && p.diasPrescricao <= 30);
  const alertasWarn = ativos.filter(p => p.diasPrescricao > 30 && p.diasPrescricao <= 90);
  const precisamConsulta = ativos.filter(p => { if (!p.dtConsulta) return true; return (Date.now() - new Date(p.dtConsulta + "T12:00:00").getTime()) / 864e5 >= 15; });
  const docsFaltantes = ativos.filter(p => { const arqs = p.arquivos || []; return !arqs.some(a => a.tipo === "CNH" && a.url); });
  const inadimplentes = ativos.filter(p => p.saldoPagar > 0).sort((a, b) => b.saldoPagar - a.saldoPagar);

  // ── Livro Caixa: normalizar movimentações ──
  const parseMesData = (mes) => {
    if (!mes) return "2025-01-15";
    const [m, y] = mes.split("/");
    const meses = { Jan:"01", Fev:"02", Mar:"03", Abr:"04", Mai:"05", Jun:"06", Jul:"07", Ago:"08", Set:"09", Out:"10", Nov:"11", Dez:"12" };
    return "20" + y + "-" + (meses[m] || "01") + "-15";
  };
  const todasMovimentacoes = fluxo.flatMap(m => {
    if (m.descricao && m.data) {
      // Estornável pelo caixa só o que NÃO tem contrapartida no processo.
      // Honorário e seus estornos devem ser revertidos no próprio processo (senão o caixa
      // diverge do saldo do cliente). Estornos e cobranças-de-processo também ficam de fora.
      const naoEstornavel = ["honorario", "estorno", "estorno_honorario", "cobranca"];
      return [{ id: m.id, data: m.data, descricao: m.descricao, valor: m.valor || 0, tipo: m.tipo || "entrada", categoria: m.categoria || "", estornavel: !naoEstornavel.includes(m.categoria) }];
    } else if (m.mes) {
      // Agregados mensais (dados-semente) → só leitura, não estornáveis.
      const dt = parseMesData(m.mes);
      const arr = [];
      if ((m.entradas || 0) > 0) arr.push({ id: m.id + "_e", data: dt, descricao: "Receitas " + m.mes, valor: m.entradas, tipo: "entrada", estornavel: false });
      if ((m.saidas || 0) > 0) arr.push({ id: m.id + "_s", data: dt, descricao: "Despesas " + m.mes, valor: m.saidas, tipo: "saida", estornavel: false });
      return arr;
    }
    return [];
  }).sort((a, b) => b.data.localeCompare(a.data));

  const filtrarPorPeriodo = (lista, periodo) => {
    if (periodo === "tudo") return lista;
    const hoje = new Date();
    const dias = { "30d": 30, "6m": 180, "12m": 365 }[periodo] || 9999;
    const limite = new Date(hoje.getTime() - dias * 864e5).toISOString().split("T")[0];
    return lista.filter(m => m.data >= limite);
  };
  const movFiltradas = filtrarPorPeriodo(todasMovimentacoes, periodoFin);
  const receitasPeriodo = movFiltradas.filter(m => m.tipo === "entrada").reduce((s, m) => s + m.valor, 0);
  const despesasPeriodo = movFiltradas.filter(m => m.tipo === "saida").reduce((s, m) => s + m.valor, 0);
  const saldoPeriodo = receitasPeriodo - despesasPeriodo;
  // ── Caixa (fonte única): tudo derivado do livro caixa / fluxo_caixa ──
  const receitasCaixa = todasMovimentacoes.filter(m => m.tipo === "entrada").reduce((s, m) => s + m.valor, 0);
  const despesasCaixa = todasMovimentacoes.filter(m => m.tipo === "saida").reduce((s, m) => s + m.valor, 0);
  const totalDespesas = despesasCaixa;
  const saldoLiquido = receitasCaixa - despesasCaixa;
  // Gráfico mensal derivado do livro caixa (agrega TODOS os lançamentos por mês —
  // agregados-semente + individuais — em vez de iterar o fluxo cru, que mistura formatos).
  const MES_ABBR = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const fluxoMensal = (() => {
    const acc = {};
    todasMovimentacoes.forEach(m => {
      const ym = (m.data || "").slice(0, 7);
      if (!ym) return;
      if (!acc[ym]) acc[ym] = { ym, entradas: 0, saidas: 0 };
      if (m.tipo === "entrada") acc[ym].entradas += m.valor; else acc[ym].saidas += m.valor;
    });
    return Object.values(acc).sort((a, b) => a.ym.localeCompare(b.ym)).slice(-8)
      .map(o => ({ ...o, mes: MES_ABBR[(+o.ym.slice(5, 7)) - 1] + "/" + o.ym.slice(2, 4) }));
  })();
  const prescricao7d = ativos.filter(p => p.diasPrescricao > 0 && p.diasPrescricao <= 7);
  const processosPorStatus = {};
  ativos.forEach(p => { processosPorStatus[p.status] = (processosPorStatus[p.status] || 0) + 1; });



  const filtered = processos.filter(p => {
    const ms = p.cliente.toLowerCase().includes(search.toLowerCase()) || p.ait.toLowerCase().includes(search.toLowerCase());
    const mf = filterStatus === "TODOS" || p.status === filterStatus;
    return ms && mf;
  });
  const statuses = ["TODOS", ...new Set(processos.map(p => p.status))];

  const handleUpload = (processoId, tipoDoc) => { if (!canEditProc) return notify("Sem permissão para enviar documentos", "err"); setUploadTarget({ processoId, tipoDoc }); setShowUpload(true); setUploadFiles([]); };
  // Seleção validada (single/múltiplo) — avisa na hora se for grande ou tipo inválido.
  const selecionarArquivo = (e, setter) => {
    const file = e.target.files[0] || null;
    if (file) { const err = arquivoInvalido(file); if (err) { notify(err, "err"); e.target.value = ""; setter(null); return; } }
    setter(file);
  };
  const selecionarArquivos = (e) => {
    const files = Array.from(e.target.files);
    const bad = files.map(arquivoInvalido).find(Boolean);
    if (bad) { notify(bad, "err"); e.target.value = ""; setUploadFiles([]); return; }
    setUploadFiles(files);
  };
  // ── Visualizar e Deletar Arquivos ──
  const handleViewArquivo = (url) => { if (url) window.open(url, "_blank"); };
  const handleDeleteArquivo = async (processoId, arquivo) => {
    if (!canEditProc) return notify("Sem permissão para excluir arquivos", "err");
    if (!window.confirm("Deletar este arquivo?")) return;
    const proc = processos.find(p => p.id === processoId);
    if (!proc) return;
    try {
      const novosArquivos = (proc.arquivos || []).filter(a => a.url !== arquivo.url);
      const docKey = canonDocKey(arquivo.tipo);
      // Só apaga o flag se não restar nenhum arquivo que mapeie para a mesma chave.
      const hasRemaining = novosArquivos.some(a => canonDocKey(a.tipo) === docKey);
      // Remove também do Storage (não deixa arquivo órfão).
      if (arquivo.caminho) { try { await deleteObject(storageRef(storage, arquivo.caminho)); } catch {} }
      const flagPatch = (docKey && !hasRemaining) ? { [docKey]: "" } : {};
      await fbUpdateProcesso(processoId, { arquivos: novosArquivos, ...flagPatch });
      if (selectedProcess?.id === processoId) setSelectedProcess(prev => ({ ...prev, arquivos: novosArquivos, ...flagPatch }));
      notify("Arquivo removido");
    } catch { notify("Erro ao remover arquivo", "err"); }
  };

  const confirmUpload = async () => {
    if (!canEditProc) return notify("Sem permissão para enviar arquivos", "err");
    if (!uploadTarget || uploadFiles.length === 0) return;
    setImportando(true);
    try {
      const proc = processos.find(p => p.id === uploadTarget.processoId);
      if (proc) {
        const docKey = canonDocKey(uploadTarget.tipoDoc);
        if (!docKey) { notify("Tipo de documento inválido", "err"); setImportando(false); return; }
        // Upload real para Firebase Storage
        const uploadResults = [];
        for (const file of uploadFiles) {
          const result = await uploadArquivo(uploadTarget.processoId, uploadTarget.tipoDoc, file);
          uploadResults.push(result);
        }
        // Remove arquivos antigos QUE MAPEIAM PARA A MESMA CHAVE (substitui, mesmo com grafia diferente).
        const antigos = (proc.arquivos || []).filter(a => canonDocKey(a.tipo) === docKey);
        const filteredArquivos = (proc.arquivos || []).filter(a => canonDocKey(a.tipo) !== docKey);
        const novosArquivos = [...filteredArquivos, ...uploadResults];
        await fbUpdateProcesso(uploadTarget.processoId, { [docKey]: "doc", arquivos: novosArquivos });
        if (selectedProcess?.id === uploadTarget.processoId) setSelectedProcess(prev => ({ ...prev, [docKey]: "doc", arquivos: novosArquivos }));
        // Apaga os blobs substituídos do Storage (evita órfãos).
        for (const a of antigos) { if (a.caminho) { try { await deleteObject(storageRef(storage, a.caminho)); } catch {} } }
        notify("Documento enviado");
      }
      setShowUpload(false); setUploadTarget(null); setUploadFiles([]);
    } catch (e) { console.error("Upload falhou:", e); notify("Falha no upload: " + (e?.code || e?.message || "erro desconhecido"), "err"); }
    setImportando(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#fff", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: isMobile ? "column" : "row" }}>
      <style>{GS}</style>

      {/* ── Sidebar (vira topo no mobile) ── */}
      <div style={{ width: isMobile ? "100%" : 220, minHeight: isMobile ? "auto" : "100vh", padding: isMobile ? "12px 16px" : "20px 14px", background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.04))", borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)", borderBottom: isMobile ? "1px solid rgba(255,255,255,0.08)" : "none", display: "flex", flexDirection: "column", gap: 3, flexShrink: 0, position: isMobile ? "sticky" : "static", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? 0 : "0 8px 18px", borderBottom: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)", marginBottom: isMobile ? 0 : 10 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}><span style={{ color: "#22d3ee" }}>Lex</span>Driver</div>
            {!isMobile && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textTransform: "uppercase", marginTop: 2 }}>Recursos de Trânsito</div>}
          </div>
          {isMobile && <button onClick={() => setMobileNav(v => !v)} style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 18, cursor: "pointer", fontFamily: "inherit" }}>{mobileNav ? "✕" : "☰"}</button>}
        </div>

        {(!isMobile || mobileNav) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: isMobile ? 10 : 0, flex: isMobile ? "none" : 1 }}>
            <NavItem icon="📊" label="Dashboard" active={page === "dashboard"} onClick={() => { setPage("dashboard"); setMobileNav(false); }} />
            <NavItem icon="📁" label="Processos" active={page === "processos"} onClick={() => { setPage("processos"); setMobileNav(false); }} />
            {canVerFin && <NavItem icon="💰" label="Financeiro" active={page === "financeiro"} onClick={() => { setPage("financeiro"); setMobileNav(false); }} />}
            <NavItem icon="📎" label="Documentos" active={page === "documentos"} onClick={() => { setPage("documentos"); setMobileNav(false); }} />
            <NavItem icon="🔔" label="Alertas" active={page === "alertas"} onClick={() => { setPage("alertas"); setMobileNav(false); }} badge={alertasCrit.length} />
            {canVerFin && <NavItem icon="💳" label="Cobranças" active={page === "cobrancas"} onClick={() => { setPage("cobrancas"); setMobileNav(false); }} badge={cobrancas.filter(c => c.status === "atrasado").length} />}
            {canGerirUsers && <NavItem icon="⚙️" label="Configurações" active={page === "config"} onClick={() => { setPage("config"); setMobileNav(false); }} />}

            <div style={{ marginTop: isMobile ? 12 : "auto", paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #22d3ee, #0891b2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{(user.nome || user.email || "?")[0].toUpperCase()}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{user.nome}</div>
                  <div style={{ fontSize: 10, color: "#22d3ee", textTransform: "capitalize" }}>{user.role || "—"}</div>
                </div>
              </div>
              <button onClick={onLogout} style={{ width: "100%", padding: "8px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.15)", color: "#ef4444", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Sair</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Main ── */}
      <div style={{ flex: 1, padding: isMobile ? "16px 14px" : "24px 28px", overflowY: "auto", maxHeight: isMobile ? "none" : "100vh", minWidth: 0 }}>

        {/* ═══ LOADING ═══ */}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", animation: "fadeIn 0.4s" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 40, height: 40, border: "3px solid rgba(34,211,238,0.2)", borderTopColor: "#22d3ee", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>Carregando dados do Firestore...</div>
            </div>
          </div>
        )}

        {/* ═══ EMPTY STATE — primeiro acesso ═══ */}
        {!loading && processos.length === 0 && page === "dashboard" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", animation: "fadeIn 0.4s" }}>
            <div style={{ textAlign: "center", maxWidth: 400 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🗄️</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Banco de dados vazio</h2>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 24, lineHeight: 1.6 }}>É a primeira vez que você acessa. Quer importar os dados da sua planilha pra começar?</p>
              <Btn onClick={handleImport} disabled={importando} style={{ padding: "14px 32px", fontSize: 15 }}>
                {importando ? "Importando..." : "Importar dados da planilha"}
              </Btn>
              <div style={{ marginTop: 16 }}>
                <Btn variant="secondary" onClick={() => setPage("processos")} style={{ fontSize: 12 }}>Ou começar do zero</Btn>
              </div>
            </div>
          </div>
        )}

        {/* ═══ DASHBOARD ═══ */}
        {!loading && page === "dashboard" && processos.length > 0 && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.5 }}>Olá, {user.nome}</h1>
                <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>Dashboard · {new Date().toLocaleDateString("pt-BR")}</p>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {alertasCrit.length > 0 && <Pill text={`${alertasCrit.length} crítico`} color="#ef4444" />}
                {alertasWarn.length > 0 && <Pill text={`${alertasWarn.length} atenção`} color="#f59e0b" />}
                {precisamConsulta.length > 0 && <Pill text={`${precisamConsulta.length} consulta`} color="#22d3ee" />}
              </div>
            </div>

            {/* ── ALERTAS DE URGÊNCIA ── */}
            {(prescricao7d.length > 0 || alertasCrit.length > 0 || docsFaltantes.length > 0) && (
              <div style={{ marginBottom: 20, padding: 16, borderRadius: 14, background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#ef4444", marginBottom: 10 }}>🚨 Requer Ação Imediata</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {prescricao7d.length > 0 && <div onClick={() => setPage("processos")} style={{ cursor: "pointer", padding: "8px 14px", borderRadius: 10, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>⏰</span><div><div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444" }}>{prescricao7d.length} processo(s) prescrevendo em 7 dias</div>{prescricao7d.slice(0, 2).map(p => <div key={p.id} style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{p.cliente} — {p.diasPrescricao}d</div>)}</div></div>}
                  {alertasCrit.length > 0 && <div onClick={() => setPage("alertas")} style={{ cursor: "pointer", padding: "8px 14px", borderRadius: 10, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>⚠️</span><div><div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{alertasCrit.length} em prescrição crítica (≤30d)</div></div></div>}
                  {docsFaltantes.length > 0 && <div onClick={() => setPage("documentos")} style={{ cursor: "pointer", padding: "8px 14px", borderRadius: 10, background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.15)", display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 16 }}>📄</span><div><div style={{ fontSize: 12, fontWeight: 700, color: "#22d3ee" }}>{docsFaltantes.length} sem CNH anexada</div></div></div>}
                </div>
              </div>
            )}

            {/* ── KPIs PRINCIPAL ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 12, marginBottom: 20 }}>
              <KPI label="Ativos" value={ativos.length} sub={Object.entries(processosPorStatus).map(([k,v]) => statusAbrev(k)+":"+v).join(" · ")} color="#22d3ee" icon="◧" />
              {canVerFin && <KPI label="Saldo" value={fmt(saldoLiquido)} sub="receitas - despesas" color={saldoLiquido >= 0 ? "#22c55e" : "#ef4444"} icon="◎" />}
              {canVerFin && <KPI label="Receitas" value={fmt(receitasCaixa)} sub={`caixa · faturado ${fmt(totalHon)}`} color="#22c55e" icon="↑" />}
              {canVerFin && <KPI label="Despesas" value={fmt(totalDespesas)} color="#f87171" icon="↓" />}
              {canVerFin
                ? <KPI label="A Receber" value={fmt(totalReceber)} sub={`${inadimplentes.length} cliente(s)`} color="#f59e0b" icon="⏳" />
                : <KPI label="Prescrição crítica" value={alertasCrit.length} sub="≤ 30 dias" color="#ef4444" icon="⏰" />}
            </div>

            {/* ── GRID: Processos por Status + Fluxo + Cobranças ── */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : (canVerFin ? "1fr 1.5fr 1fr" : "1fr"), gap: 14, marginBottom: 20 }}>
              <Card>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Processos por Fase</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {Object.entries(processosPorStatus).sort((a,b) => b[1] - a[1]).map(([status, count]) => (
                    <div key={status} onClick={() => { setPage("processos"); setFilterStatus(status); }} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                      <Pill text={status} color={statusColor(status)} />
                      <span style={{ marginLeft: "auto", fontSize: 16, fontWeight: 800, color: "#fff", fontFamily: "'JetBrains Mono'" }}>{count}</span>
                    </div>
                  ))}
                </div>
              </Card>
              {canVerFin && <Card>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Fluxo de Caixa</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100 }}>
                  {fluxoMensal.length === 0 ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: "auto" }}>Sem movimentações ainda</div> : fluxoMensal.map((d, i) => { const max = Math.max(...fluxoMensal.map(x => Math.max(x.entradas, x.saidas)), 1); return (
                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, gap: 2 }}>
                      <div style={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                        <div title={`${d.mes} · Receitas ${fmt(d.entradas)}`} style={{ width: 14, height: Math.max(4, (d.entradas / max) * 70), background: "linear-gradient(180deg, #22d3ee, #0891b2)", borderRadius: "3px 3px 1px 1px" }} />
                        <div title={`${d.mes} · Despesas ${fmt(d.saidas)}`} style={{ width: 14, height: Math.max(4, (d.saidas / max) * 70), background: "linear-gradient(180deg, #f87171, #dc2626)", borderRadius: "3px 3px 1px 1px" }} />
                      </div>
                      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.35)" }}>{d.mes}</span>
                    </div>
                  ); })}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 10, justifyContent: "center" }}>
                  <span style={{ fontSize: 9, color: "#22d3ee" }}>● Receitas</span>
                  <span style={{ fontSize: 9, color: "#f87171" }}>● Despesas</span>
                </div>
              </Card>}
              {canVerFin && <Card>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Cobranças</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {[{ l: "Pagas", c: cobrancas.filter(c => c.status === "pago").length, v: cobrancas.filter(c => c.status === "pago").reduce((s,c) => s + c.valor, 0), clr: "#22c55e" }, { l: "Pendentes", c: cobrancas.filter(c => c.status === "pendente").length, v: cobrancas.filter(c => c.status === "pendente").reduce((s,c) => s + c.valor, 0), clr: "#f59e0b" }, { l: "Atrasadas", c: cobrancas.filter(c => c.status === "atrasado").length, v: cobrancas.filter(c => c.status === "atrasado").reduce((s,c) => s + c.valor, 0), clr: "#ef4444" }].map(x => (
                    <div key={x.l} onClick={() => setPage("cobrancas")} style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, background: x.clr + "08", border: "1px solid " + x.clr + "1a" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: x.clr }} /><span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{x.l}</span></div>
                      <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 800, color: x.clr, fontFamily: "'JetBrains Mono'" }}>{x.c}</div><div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>{fmt(x.v)}</div></div>
                    </div>
                  ))}
                </div>
              </Card>}
            </div>

            {/* ── GRID: Inadimplentes + Consultas Pendentes ── */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : (canVerFin ? "1fr 1fr" : "1fr"), gap: 14 }}>
              {canVerFin && <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Top Inadimplentes</div>
                  {inadimplentes.length > 0 && <Pill text={fmt(totalReceber)} color="#ef4444" />}
                </div>
                {inadimplentes.length === 0 ? <div style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Sem inadimplentes ✓</div> : (
                  <div style={{ display: "grid", gap: 6 }}>{inadimplentes.slice(0, 5).map(p => (
                    <div key={p.id} onClick={() => { setPage("processos"); setSelectedProcess(p); }} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.1)" }}>
                      <div><span style={{ fontSize: 12, fontWeight: 600 }}>{p.cliente}</span><div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 1 }}>{p.formaPgto}</div></div>
                      <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", fontFamily: "'JetBrains Mono'" }}>{fmt(p.saldoPagar)}</div><div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>de {fmt(p.honorario)}</div></div>
                    </div>
                  ))}</div>
                )}
              </Card>}
              <Card>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Consultas Pendentes (≥ 15 dias)</div>
                {precisamConsulta.length === 0 ? <div style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Tudo em dia ✓</div> : (
                  <div style={{ display: "grid", gap: 6 }}>{precisamConsulta.slice(0, 5).map(p => (
                    <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, background: "rgba(34,211,238,0.04)", border: "1px solid rgba(34,211,238,0.1)" }}>
                      <div><span style={{ fontSize: 12, fontWeight: 600 }}>{p.cliente}</span><span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginLeft: 8 }}>{p.ait}</span></div>
                      <span style={{ fontSize: 11, color: "#22d3ee", fontFamily: "'JetBrains Mono'" }}>Última: {fmtDate(p.dtConsulta)}</span>
                    </div>
                  ))}</div>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* ═══ PROCESSOS ═══ */}
        {page === "processos" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div><h1 style={{ fontSize: 24, fontWeight: 800 }}>Processos</h1><p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>Gestão completa de recursos</p></div>
              {canEditProc && <Btn onClick={() => setShowNewProcesso(true)}>+ Novo Processo</Btn>}
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <input type="text" placeholder="Buscar cliente ou AIT..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", fontSize: 13, outline: "none" }} />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", fontSize: 13, outline: "none" }}>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ borderRadius: 16, overflowX: "auto", border: "1px solid rgba(255,255,255,0.06)" }}>
              <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: "rgba(255,255,255,0.03)" }}>
                  {["AIT", "Cliente", "Status", "Tipo", "Prescr.", "Honorário", "Saldo", "Docs"].map(h => (
                    <th key={h} style={{ padding: "11px 12px", textAlign: "left", fontWeight: 700, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{filtered.map((p, i) => (
                  <tr key={p.id} onClick={() => setSelectedProcess(p)} style={{ cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)", background: selectedProcess?.id === p.id ? "rgba(34,211,238,0.06)" : "transparent", transition: "background 0.15s" }}>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{p.ait.length > 14 ? p.ait.slice(0, 14) + "…" : p.ait}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 600, fontSize: 12 }}>{p.cliente}</td>
                    <td style={{ padding: "10px 12px" }}><Pill text={p.status} color={statusColor(p.status)} /></td>
                    <td style={{ padding: "10px 12px", color: "rgba(255,255,255,0.5)" }}>{p.tipo}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontWeight: 600, color: prescricaoColor(p.diasPrescricao) }}>{p.diasPrescricao > 0 ? `${p.diasPrescricao}d` : "—"}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'" }}>{fmt(p.honorario)}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", color: p.saldoPagar > 0 ? "#f59e0b" : "#22c55e" }}>{fmt(p.saldoPagar)}</td>
                    <td style={{ padding: "10px 12px" }}><div style={{ display: "flex", gap: 2 }}>{(() => { const ds = docStatus(p); return [ds.cnh, ds.defesaPrevia, ds.jari, ds.cetran].map((ok, j) => <div key={j} title={["CNH","Defesa Prévia","JARI","CETRAN"][j]} style={{ width: 8, height: 8, borderRadius: 2, background: ok ? "#22c55e55" : "rgba(255,255,255,0.06)", border: `1px solid ${ok ? "#22c55e33" : "rgba(255,255,255,0.06)"}` }} />); })()}</div></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 13 }}>
                    {processos.length === 0 ? "Nenhum processo cadastrado ainda." : `Nenhum processo encontrado${search ? ` para "${search}"` : ""}${filterStatus !== "TODOS" ? ` em ${filterStatus}` : ""}.`}
                  </td></tr>
                )}
                </tbody>
              </table>
            </div>

            {selectedProcess && (
              <Card style={{ marginTop: 16, animation: "slideIn 0.3s" }}>
                {/* Header com telefone */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800 }}>{selectedProcess.cliente}</h3>
                    <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontFamily: "'JetBrains Mono'", marginTop: 3 }}>{selectedProcess.ait}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                      {selectedProcess.telefone ? (
                        <span style={{ fontSize: 12, color: "#22d3ee", display: "flex", alignItems: "center", gap: 4 }}>📱 {selectedProcess.telefone}</span>
                      ) : (
                        canEditProc && <button onClick={() => { const tel = prompt("Telefone do cliente (com DDD):"); if (tel) updateProcesso(selectedProcess.id, { telefone: tel }); }} style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>+ Adicionar telefone</button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                    {canEditProc && <Btn variant="secondary" onClick={() => setShowEditProcesso({ ...selectedProcess })} style={{ padding: "6px 14px", fontSize: 11, height: 30 }}>✎ Editar</Btn>}
                    <Btn variant="secondary" onClick={() => { setSelectedProcess(null); setShowFinPanel(false); setConsultaOk(false); }} style={{ padding: "6px 12px", fontSize: 11, height: 30 }}>✕</Btn>
                  </div>
                </div>

                {/* Botões grandes de ação — fácil pro sócio */}
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
                  <button disabled={!canEditProc} onClick={() => {
                    if (!consultaOk && canEditProc) {
                      const hoje = new Date().toISOString().split("T")[0];
                      updateProcesso(selectedProcess.id, { dtConsulta: hoje });
                      setConsultaOk(true);
                      notify("Consulta registrada");
                      setTimeout(() => setConsultaOk(false), 3000);
                    }
                  }} style={{ padding: "14px 12px", borderRadius: 12, background: consultaOk ? "rgba(34,197,94,0.15)" : "rgba(34,211,238,0.08)", border: `1px solid ${consultaOk ? "rgba(34,197,94,0.3)" : "rgba(34,211,238,0.2)"}`, cursor: canEditProc ? "pointer" : "not-allowed", opacity: canEditProc ? 1 : 0.5, fontFamily: "inherit", color: "#fff", textAlign: "center", transition: "all 0.3s" }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{consultaOk ? "✅" : "🔍"}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: consultaOk ? "#22c55e" : "#22d3ee" }}>{consultaOk ? "Registrado!" : "Registrar Consulta"}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{consultaOk ? `Atualizado: ${new Date().toLocaleDateString("pt-BR")}` : `Última: ${fmtDate(selectedProcess.dtConsulta)}`}</div>
                  </button>
                  {canVerFin ? (
                  <button onClick={() => setShowFinPanel(!showFinPanel)} style={{ padding: "14px 12px", borderRadius: 12, background: showFinPanel ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.08)", border: `1px solid ${showFinPanel ? "#22c55e55" : "rgba(34,197,94,0.2)"}`, cursor: "pointer", fontFamily: "inherit", color: "#fff", textAlign: "center" }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>💰</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#22c55e" }}>Financeiro</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>Saldo: {fmt(selectedProcess.saldoPagar)}</div>
                  </button>
                  ) : <div />}
                  <div>
                    <label style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}>Status</label>
                    <select value={selectedProcess.status} disabled={!canEditProc} onChange={e => handleStatusChange(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12, opacity: canEditProc ? 1 : 0.5 }}>
                      <option>DEFESA PRÉVIA</option><option>INDEFERIDO DEFESA PRÉVIA</option><option>RECURSO JARI</option><option>INDEFERIDO JARI</option><option>RECURSO CETRAN</option><option>INDEFERIDO CETRAN</option><option>DEFERIDO</option><option>INDEFERIDO</option><option>CANCELADO</option>
                    </select>
                  </div>
                </div>

                {/* ── PAINEL FINANCEIRO (expande ao clicar no botão 💰) ── */}
                {showFinPanel && (
                  <div style={{ marginBottom: 18, padding: 18, borderRadius: 14, background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.15)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#22c55e", marginBottom: 14 }}>💰 Financeiro — {selectedProcess.cliente}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 16 }}>
                      <div style={{ padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>HONORÁRIO</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: "'JetBrains Mono'" }}>{fmt(selectedProcess.honorario)}</div>
                      </div>
                      <div style={{ padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>JÁ PAGO</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e", fontFamily: "'JetBrains Mono'" }}>{fmt(selectedProcess.honorario - selectedProcess.saldoPagar)}</div>
                      </div>
                      <div style={{ padding: 12, borderRadius: 10, background: selectedProcess.saldoPagar > 0 ? "rgba(239,68,68,0.06)" : "rgba(34,197,94,0.06)", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>FALTA PAGAR</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: selectedProcess.saldoPagar > 0 ? "#f59e0b" : "#22c55e", fontFamily: "'JetBrains Mono'" }}>{fmt(selectedProcess.saldoPagar)}</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 16 }}>
                      <div>
                        <label style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}>Forma de Pagamento</label>
                        <select value={selectedProcess.formaPgto} onChange={e => { updateProcesso(selectedProcess.id, { formaPgto: e.target.value }); setSelectedProcess(prev => ({ ...prev, formaPgto: e.target.value })); }} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}>
                          <option>A VISTA</option><option>PARCELAMENTO (2X)</option><option>PARCELAMENTO (3X)</option><option>PARCELAMENTO (4X)</option><option>PARCELAMENTO (6X)</option><option>PARCELAMENTO (10X)</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}>Alterar Honorário (R$)</label>
                        <input type="number" defaultValue={selectedProcess.honorario} onBlur={e => { const v = parseFloat(e.target.value) || 0; const diff = v - selectedProcess.honorario; updateProcesso(selectedProcess.id, { honorario: v, saldoPagar: Math.max(0, selectedProcess.saldoPagar + diff) }); setSelectedProcess(prev => ({ ...prev, honorario: v, saldoPagar: Math.max(0, prev.saldoPagar + diff) })); }} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                      </div>
                    </div>
                    {selectedProcess.saldoPagar > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Registrar Pagamento</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <input type="number" inputMode="decimal" placeholder="Valor recebido" value={parcelaValor} onChange={e => setParcelaValor(e.target.value)} style={{ flex: "1 1 140px", minWidth: 0, padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 14, fontFamily: "'JetBrains Mono'" }} />
                          <Btn onClick={() => registrarParcela(selectedProcess.id, parseFloat(parcelaValor), `Parcela de ${fmt(parseFloat(parcelaValor) || 0)}`)} disabled={!parcelaValor || importando} style={{ padding: "12px 20px", fontSize: 13 }}>{importando ? "..." : "Registrar"}</Btn>
                          <Btn variant="secondary" onClick={() => registrarParcela(selectedProcess.id, selectedProcess.saldoPagar, "Quitação total")} disabled={importando} style={{ padding: "12px 16px", fontSize: 12 }}>Quitar tudo</Btn>
                        </div>
                      </div>
                    )}
                    {selectedProcess.saldoPagar === 0 && (
                      <div style={{ padding: 14, borderRadius: 10, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", textAlign: "center", marginBottom: 16 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>✓ Cliente em dia — Honorário quitado</span>
                      </div>
                    )}
                    {selectedProcess.pagamentos && selectedProcess.pagamentos.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 8 }}>Histórico de Pagamentos</div>
                        <div style={{ display: "grid", gap: 4 }}>
                          {selectedProcess.pagamentos.map((pg, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: pg.estornado ? "rgba(239,68,68,0.04)" : "rgba(255,255,255,0.02)", opacity: pg.estornado ? 0.6 : 1 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: pg.estornado ? "#ef444415" : "#22c55e15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: pg.estornado ? "#ef4444" : "#22c55e" }}>{pg.estornado ? "✕" : "✓"}</div>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, textDecoration: pg.estornado ? "line-through" : "none" }}>{fmt(pg.valor)}</span>
                                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginLeft: 8 }}>{pg.obs}</span>
                                {pg.estornado && <div style={{ fontSize: 9, color: "#ef4444", marginTop: 2 }}>Estornado em {pg.dataEstorno} — {pg.motivoEstorno}</div>}
                              </div>
                              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono'" }}>{fmtDate(pg.data)}</span>
                              {!pg.estornado && <button onClick={() => estornarPagamento(selectedProcess.id, i)} style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Estornar</button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Datas */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 14, marginBottom: 18 }}>
                  {[{ l: "Infração", v: fmtDate(selectedProcess.dtInfracao) }, { l: "Recurso", v: fmtDate(selectedProcess.dtRecurso) }, { l: "Consulta", v: fmtDate(selectedProcess.dtConsulta) }, { l: "Prescrição", v: selectedProcess.diasPrescricao > 0 ? `${selectedProcess.diasPrescricao}d` : "Fin." }].map(x => (
                    <div key={x.l}><div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>{x.l}</div><div style={{ fontSize: 14, fontWeight: 600 }}>{x.v}</div></div>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>DOCUMENTOS DO PROCESSO</div>

                {/* CNH */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>Documento do Condutor</div>
                  {(() => { const hasFile = (selectedProcess.arquivos || []).some(a => a.tipo === "CNH" && a.url); return (
                  <button onClick={() => handleUpload(selectedProcess.id, "CNH")} style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: hasFile ? "rgba(34,197,94,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${hasFile ? "#22c55e33" : "rgba(255,255,255,0.08)"}`, cursor: "pointer", fontFamily: "inherit", color: "#fff", display: "flex", alignItems: "center", gap: 12, textAlign: "left" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: hasFile ? "#22c55e18" : "rgba(255,255,255,0.04)", fontSize: 16 }}>{hasFile ? "✓" : "↑"}</div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: hasFile ? "#22c55e" : "#fff" }}>CNH</div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{hasFile ? "Arquivo enviado · clique pra substituir" : "Clique para enviar"}</div></div>
                  </button>
                  ); })()}
                </div>

                {/* Instâncias recursais */}
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 6 }}>Instâncias Recursais (protocolo / recibo)</div>
                <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
                  {[
                    { key: "defesaPrevia", label: "Defesa Prévia", desc: "1ª instância — Órgão autuador" },
                    { key: "jari", label: "Recurso JARI", desc: "2ª instância — Junta Administrativa" },
                    { key: "cetran", label: "Recurso CETRAN", desc: "3ª instância — Conselho Estadual" },
                  ].map(d => {
                    const hasFile = (selectedProcess.arquivos || []).some(a => (a.tipo === d.label || a.tipo === d.label.replace("Recurso ", "")) && a.url);
                    return (
                      <button key={d.key} onClick={() => handleUpload(selectedProcess.id, d.label)} style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: hasFile ? "rgba(34,197,94,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${hasFile ? "#22c55e33" : "rgba(255,255,255,0.08)"}`, cursor: "pointer", fontFamily: "inherit", color: "#fff", display: "flex", alignItems: "center", gap: 12, textAlign: "left" }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: hasFile ? "#22c55e18" : "rgba(255,255,255,0.04)", fontSize: 16 }}>{hasFile ? "✓" : "↑"}</div>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: hasFile ? "#22c55e" : "#fff" }}>{d.label}</div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{hasFile ? "Arquivo enviado · clique pra substituir" : d.desc}</div></div>
                        {hasFile && <span style={{ fontSize: 10, color: "#22c55e", fontFamily: "'JetBrains Mono'" }}>PDF</span>}
                      </button>
                    );
                  })}
                </div>

                {selectedProcess.arquivos && selectedProcess.arquivos.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 6 }}>ARQUIVOS ENVIADOS</div>
                    {selectedProcess.arquivos.map((a, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", marginBottom: 4, fontSize: 11 }}>
                        <span style={{ color: "#22d3ee", fontWeight: 600, minWidth: 80 }}>{a.tipo}</span>
                        <span style={{ flex: 1, color: "rgba(255,255,255,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.nome || "arquivo"}</span>
                        <button onClick={() => handleViewArquivo(a.url)} style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.2)", color: "#22d3ee", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit" }}>Abrir</button>
                        <button onClick={() => handleDeleteArquivo(selectedProcess.id, a)} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit" }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {/* ═══ FINANCEIRO ═══ */}
        {page === "financeiro" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800 }}>Financeiro</h1>
              <div style={{ display: "flex", gap: 8 }}>
                {canEditFin && <Btn variant="secondary" onClick={() => setShowRetirada(true)} style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)", color: "#a78bfa" }}>💸 Retirada Sócios</Btn>}
                {canEditFin && <Btn onClick={() => setShowNewFinanceiro(true)}>+ Nova Entrada / Saída</Btn>}
              </div>
            </div>

            {/* ── Filtro por Período ── */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {["30d", "6m", "12m", "tudo"].map(p => (
                <button key={p} onClick={() => setPeriodoFin(p)} style={{ padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: periodoFin === p ? "rgba(34,211,238,0.15)" : "rgba(255,255,255,0.04)", border: "1px solid " + (periodoFin === p ? "rgba(34,211,238,0.3)" : "rgba(255,255,255,0.08)"), color: periodoFin === p ? "#22d3ee" : "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit" }}>
                  {{ "30d": "30 dias", "6m": "6 meses", "12m": "12 meses", "tudo": "Tudo" }[p]}
                </button>
              ))}
            </div>

            {/* ── KPIs do Período ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 12, marginBottom: 20 }}>
              <KPI label="Saldo Período" value={fmt(saldoPeriodo)} color={saldoPeriodo >= 0 ? "#22c55e" : "#ef4444"} icon="◎" />
              <KPI label="Receitas" value={fmt(receitasPeriodo)} sub={movFiltradas.filter(m => m.tipo === "entrada").length + " mov."} color="#22c55e" icon="↑" />
              <KPI label="Despesas" value={fmt(despesasPeriodo)} sub={movFiltradas.filter(m => m.tipo === "saida").length + " mov."} color="#f87171" icon="↓" />
              <KPI label="A Receber" value={fmt(totalReceber)} sub={inadimplentes.length + " cliente(s)"} color="#f59e0b" icon="⏳" />
            </div>

            {/* ── Livro Caixa / Extrato ── */}
            <Card style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Livro Caixa</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{movFiltradas.length} movimentações</div>
              </div>
              {movFiltradas.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 13 }}>Nenhuma movimentação no período</div>
              ) : (
                <div style={{ borderRadius: 12, overflowX: "auto", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                        {["Data", "Descrição", "Tipo", "Entrada", "Saída", "Saldo", canEditFin ? "" : null].filter(h => h !== null).map((h, hi) => (
                          <th key={h || hi} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Saldo REAL de caixa: acumula sobre TODO o livro (do mais antigo ao mais
                        // recente), não só o período filtrado — senão a coluna "Saldo" zera errado.
                        const cron = [...todasMovimentacoes].reverse();
                        let acum = 0; const saldoPorId = new Map();
                        for (const m of cron) { acum += m.tipo === "entrada" ? m.valor : -m.valor; saldoPorId.set(m.id, acum); }
                        return movFiltradas.map((m, i) => (
                          <tr key={m.id || i} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                            <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{fmtDate(m.data)}</td>
                            <td style={{ padding: "10px 12px", fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>{m.descricao || "—"}</td>
                            <td style={{ padding: "10px 12px" }}><span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: m.tipo === "entrada" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: m.tipo === "entrada" ? "#22c55e" : "#ef4444", border: "1px solid " + (m.tipo === "entrada" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)") }}>{m.tipo === "entrada" ? "Entrada" : "Saída"}</span></td>
                            <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontWeight: 700, color: "#22c55e", fontSize: 12 }}>{m.tipo === "entrada" ? fmt(m.valor) : ""}</td>
                            <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontWeight: 700, color: "#ef4444", fontSize: 12 }}>{m.tipo === "saida" ? fmt(m.valor) : ""}</td>
                            <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontWeight: 700, color: (saldoPorId.get(m.id) ?? 0) >= 0 ? "rgba(255,255,255,0.7)" : "#ef4444", fontSize: 12 }}>{fmt(saldoPorId.get(m.id) ?? 0)}</td>
                            {canEditFin && <td style={{ padding: "10px 12px" }}>{m.estornavel && <button onClick={() => estornarMovimentacao(m.id)} style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Estornar</button>}</td>}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* ── Inadimplentes ── */}
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Inadimplentes</div>
                {inadimplentes.length > 0 && <Pill text={fmt(totalReceber)} color="#ef4444" />}
              </div>
              {inadimplentes.length === 0 ? <div style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Sem inadimplentes ✓</div> : (
                <div style={{ display: "grid", gap: 6 }}>{inadimplentes.map(p => (
                  <div key={p.id} onClick={() => { setPage("processos"); setSelectedProcess(p); }} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.1)" }}>
                    <div><span style={{ fontSize: 12, fontWeight: 600 }}>{p.cliente}</span><div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 1 }}>{p.formaPgto}</div></div>
                    <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", fontFamily: "'JetBrains Mono'" }}>{fmt(p.saldoPagar)}</div><div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>de {fmt(p.honorario)}</div></div>
                  </div>
                ))}</div>
              )}
            </Card>
          </div>
        )}


        {/* ═══ DOCUMENTOS ═══ */}
        {page === "documentos" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Documentos</h1>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 20 }}>Arquivos armazenados no Google Cloud Storage</p>
            <div style={{ display: "grid", gap: 10 }}>
              {ativos.map(p => { const ds = docStatus(p); const docs = [{ l: "CNH", v: ds.cnh }, { l: "Def.Prévia", v: ds.defesaPrevia }, { l: "JARI", v: ds.jari }, { l: "CETRAN", v: ds.cetran }]; const done = docs.filter(d => d.v).length; return (
                <div key={p.id} style={{ padding: "14px 18px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 700 }}>{p.cliente}</div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontFamily: "'JetBrains Mono'", marginTop: 2, wordBreak: "break-all" }}>{p.ait}</div></div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{docs.map(d => <div key={d.l} style={{ display: "flex", alignItems: "center", gap: 3 }}><div style={{ width: 16, height: 16, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", background: d.v ? "#22c55e22" : "rgba(255,255,255,0.04)", border: `1px solid ${d.v ? "#22c55e44" : "rgba(255,255,255,0.08)"}`, fontSize: 9, color: d.v ? "#22c55e" : "rgba(255,255,255,0.35)" }}>{d.v ? "✓" : "·"}</div><span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>{d.l}</span></div>)}</div>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: done === 4 ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.04)", border: `2px solid ${done === 4 ? "#22c55e" : "rgba(255,255,255,0.1)"}`, fontSize: 11, fontWeight: 800, color: done === 4 ? "#22c55e" : "rgba(255,255,255,0.4)", fontFamily: "'JetBrains Mono'" }}>{done}/4</div>
                </div>
              ); })}
            </div>
          </div>
        )}

        {/* ═══ ALERTAS ═══ */}
        {page === "alertas" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 20 }}>Alertas</h1>
            {[{ items: alertasCrit, label: "CRÍTICO — Prescrição ≤ 30 dias", clr: "#ef4444", pulse: true }, { items: alertasWarn, label: "ATENÇÃO — Prescrição ≤ 90 dias", clr: "#f59e0b" }].map(g => g.items.length > 0 && (
              <div key={g.label} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: g.clr, animation: g.pulse ? "pulse 1.5s infinite" : "none" }} /><span style={{ fontSize: 13, fontWeight: 700, color: g.clr }}>{g.label}</span></div>
                {g.items.map(p => <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, background: g.clr + "0a", border: `1px solid ${g.clr}22`, marginBottom: 6 }}><div><span style={{ fontWeight: 700 }}>{p.cliente}</span><span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginLeft: 8 }}>{p.ait}</span></div><span style={{ fontFamily: "'JetBrains Mono'", fontWeight: 800, color: g.clr, fontSize: 16 }}>{p.diasPrescricao}d</span></div>)}
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#22d3ee" }} /><span style={{ fontSize: 13, fontWeight: 700, color: "#22d3ee" }}>CONSULTA — ≥ 15 dias</span></div>
              {precisamConsulta.length === 0 ? <div style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Tudo em dia ✓</div> : precisamConsulta.map(p => <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, background: "rgba(34,211,238,0.04)", border: "1px solid rgba(34,211,238,0.1)", marginBottom: 6 }}><div><span style={{ fontWeight: 700 }}>{p.cliente}</span><span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginLeft: 8 }}>{p.ait}</span></div><span style={{ fontSize: 11, color: "#22d3ee", fontFamily: "'JetBrains Mono'" }}>Última: {fmtDate(p.dtConsulta)}</span></div>)}
            </div>
          </div>
        )}

        {/* ═══ COBRANÇAS (Pagar.me) ═══ */}
        {page === "cobrancas" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div><h1 style={{ fontSize: 24, fontWeight: 800 }}>Cobranças</h1><p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>Controle de recebimentos · PIX, Boleto e Cartão</p></div>
              {canEditFin && <Btn onClick={() => setShowNewCob(true)}>+ Nova Cobrança</Btn>}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 14, marginBottom: 24 }}>
              <KPI label="Total Cobranças" value={cobrancas.length} color="#22d3ee" icon="◉" />
              <KPI label="Pagas" value={fmt(cobrancas.filter(c => c.status === "pago").reduce((s, c) => s + c.valor, 0))} color="#22c55e" icon="✓" />
              <KPI label="Pendentes" value={fmt(cobrancas.filter(c => c.status === "pendente").reduce((s, c) => s + c.valor, 0))} color="#f59e0b" icon="⏳" />
              <KPI label="Atrasadas" value={fmt(cobrancas.filter(c => c.status === "atrasado").reduce((s, c) => s + c.valor, 0))} color="#ef4444" icon="!" />
            </div>

            <div style={{ borderRadius: 16, overflowX: "auto", border: "1px solid rgba(255,255,255,0.06)" }}>
              <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: "rgba(255,255,255,0.03)" }}>
                  {["Cliente", "Valor", "Método", "Parcela", "Vencimento", "Status", "Ações"].map(h => (
                    <th key={h} style={{ padding: "11px 12px", textAlign: "left", fontWeight: 700, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{cobrancas.map(c => (
                  <tr key={c.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{c.cliente}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontWeight: 600 }}>{fmt(c.valor)}</td>
                    <td style={{ padding: "10px 12px" }}><Pill text={c.metodo} color={c.metodo === "PIX" ? "#22c55e" : c.metodo === "Boleto" ? "#f59e0b" : "#a78bfa"} /></td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", color: "rgba(255,255,255,0.5)" }}>{c.parcela}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono'", fontSize: 11 }}>{fmtDate(c.data)}</td>
                    <td style={{ padding: "10px 12px" }}><Pill text={c.status.toUpperCase()} color={payStatusColor(c.status)} /></td>
                    <td style={{ padding: "10px 12px" }}>
                      {c.status !== "pago" && canEditFin && (
                        <div style={{ display: "flex", gap: 4 }}>
                          <Btn variant="secondary" style={{ padding: "4px 10px", fontSize: 10 }} onClick={async () => { try { await navigator.clipboard.writeText(`PIX: lexdriver@pagar.me | ${fmt(c.valor)}`); notify("Link copiado"); } catch { notify("Não foi possível copiar", "err"); } }}>Copiar link</Btn>
                          <Btn variant="secondary" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => baixarCobranca(c)}>✓ Baixar</Btn>
                        </div>
                      )}
                      {c.status === "pago" && <span style={{ fontSize: 10, color: "#22c55e" }}>✓ recebido</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>

            {/* Modal Nova Cobrança */}
            {showNewCob && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
                <Card modal style={{ width: "min(460px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
                  <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Nova Cobrança</h3>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 20 }}>Ao dar baixa, o valor abate o saldo do processo automaticamente.</p>
                  <div style={{ display: "grid", gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", letterSpacing: 1 }}>CLIENTE / PROCESSO</label>
                      <select value={novaCob.processoId} onChange={e => { const p = ativos.find(x => x.id === e.target.value); setNovaCob(c => ({ ...c, processoId: e.target.value, valor: p ? String(p.saldoPagar) : c.valor })); }} style={{ width: "100%", padding: "10px 14px", marginTop: 6, borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 13 }}>
                        <option value="">Selecione…</option>
                        {ativos.filter(p => p.saldoPagar > 0).map(p => <option key={p.id} value={p.id}>{p.cliente} — {fmt(p.saldoPagar)}</option>)}
                      </select>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                      <Input label="Valor (R$)" type="number" placeholder="0.00" value={novaCob.valor} onChange={e => setNovaCob(c => ({ ...c, valor: e.target.value }))} />
                      <div><label style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", letterSpacing: 1 }}>MÉTODO</label><select value={novaCob.metodo} onChange={e => setNovaCob(c => ({ ...c, metodo: e.target.value }))} style={{ width: "100%", padding: "10px 14px", marginTop: 6, borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 13 }}><option>PIX</option><option>Boleto</option><option>Cartão</option></select></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                      <Input label="Vencimento" type="date" value={novaCob.data} onChange={e => setNovaCob(c => ({ ...c, data: e.target.value }))} />
                      <Input label="Parcela" type="text" placeholder="1/1" value={novaCob.parcela} onChange={e => setNovaCob(c => ({ ...c, parcela: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
                    <Btn variant="secondary" onClick={() => setShowNewCob(false)}>Cancelar</Btn>
                    <Btn onClick={criarCobranca} disabled={!novaCob.processoId || !novaCob.valor}>Criar Cobrança</Btn>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ CONFIGURAÇÕES ═══ */}
        {page === "config" && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 20 }}>Configurações</h1>
            <div style={{ display: "grid", gap: 16, maxWidth: 600 }}>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Dados da Empresa</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <Input label="Nome da Empresa" type="text" defaultValue="LexDriver Recursos" />
                  <Input label="CNPJ" type="text" placeholder="00.000.000/0001-00" />
                  <Input label="Email" type="email" defaultValue={user.email} />
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Integração de Pagamento</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>
                  ⚠️ Por segurança, chaves de API (Pagar.me/gateway) <strong>nunca</strong> devem ser guardadas no navegador ou no banco — elas ficam apenas em uma função de servidor (backend). Configure-as com sua equipe técnica no ambiente do servidor, não aqui.
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Google Cloud Storage</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <Input label="Bucket" type="text" defaultValue="lexdriver-documentos" />
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>
                    Os documentos (CNH, recursos, defesas) são armazenados no Google Cloud Storage no bucket configurado acima. Cada arquivo fica em:<br />
                    <code style={{ fontSize: 11, color: "#22d3ee", fontFamily: "'JetBrains Mono'" }}>gs://lexdriver-documentos/empresa_id/processo_id/tipo/arquivo.pdf</code>
                  </div>
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Usuários e Permissões</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 16, lineHeight: 1.5 }}>
                  Gerencie o acesso dos usuários ao sistema. Novos cadastros aparecem como "pendente".
                </div>

                {/* PENDENTES — requer ação */}
                {allUsers.filter(u => u.role === "pendente").length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", letterSpacing: 0.5, marginBottom: 8 }}>⏳ AGUARDANDO APROVAÇÃO</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {allUsers.filter(u => u.role === "pendente").map(u => (
                        <div key={u.uid} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.15)" }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #f59e0b, #d97706)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>{(u.nome || "?")[0].toUpperCase()}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{u.nome || "Sem nome"}</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{u.email}</div>
                            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)" }}>Cadastro: {u.criadoEm ? new Date(u.criadoEm).toLocaleDateString("pt-BR") : "—"}</div>
                          </div>
                          {showRoleSelect === u.uid ? (
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {["admin", "processos", "financeiro", "visualizador"].map(r => (
                                <button key={r} onClick={() => approveUser(u.uid, r)} style={{ padding: "4px 10px", borderRadius: 6, background: ((r) => ({ admin: "#a78bfa", processos: "#22d3ee", financeiro: "#22c55e", visualizador: "#78716c", pendente: "#f59e0b", rejeitado: "#78716c" }[r] || "#78716c"))(r) + "18", border: "1px solid " + ((r) => ({ admin: "#a78bfa", processos: "#22d3ee", financeiro: "#22c55e", visualizador: "#78716c", pendente: "#f59e0b", rejeitado: "#78716c" }[r] || "#78716c"))(r) + "33", color: ((r) => ({ admin: "#a78bfa", processos: "#22d3ee", financeiro: "#22c55e", visualizador: "#78716c", pendente: "#f59e0b", rejeitado: "#78716c" }[r] || "#78716c"))(r), fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" }}>{r}</button>
                              ))}
                              <button onClick={() => setShowRoleSelect(null)} style={{ padding: "4px 8px", borderRadius: 6, background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={() => setShowRoleSelect(u.uid)} style={{ padding: "5px 12px", borderRadius: 8, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", color: "#22c55e", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Aprovar</button>
                              <button onClick={() => rejectUser(u.uid)} style={{ padding: "5px 12px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Rejeitar</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* USUÁRIOS ATIVOS */}
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 8 }}>USUÁRIOS ATIVOS</div>
                <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
                  {allUsers.filter(u => u.role && u.role !== "pendente" && u.role !== "rejeitado").map(u => (
                    <div key={u.uid} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.uid === user.uid ? "linear-gradient(135deg, #22d3ee, #0891b2)" : "linear-gradient(135deg, #6366f1, #4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>{(u.nome || "?")[0].toUpperCase()}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.nome || "Sem nome"} {u.uid === user.uid ? "(você)" : ""}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{u.email}</div>
                      </div>
                      <Pill text={u.role} color={((r) => ({ admin: "#a78bfa", processos: "#22d3ee", financeiro: "#22c55e", visualizador: "#78716c", pendente: "#f59e0b", rejeitado: "#78716c" }[r] || "#78716c"))(u.role)} />
                    </div>
                  ))}
                </div>

                {/* Perfis */}
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, marginBottom: 8 }}>Perfis disponíveis</div>
                <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
                  {[
                    { role: "Admin", desc: "Acesso total: processos, financeiro, documentos, configurações", clr: "#a78bfa" },
                    { role: "Processos", desc: "Cadastra processos, upload de documentos, acompanha prazos", clr: "#22d3ee" },
                    { role: "Financeiro", desc: "Financeiro, cobranças, edita dados financeiros dos clientes", clr: "#22c55e" },
                    { role: "Visualizador", desc: "Somente leitura — vê dashboard e processos", clr: "#78716c" },
                  ].map(r => (
                    <div key={r.role} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)" }}>
                      <Pill text={r.role} color={r.clr} />
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{r.desc}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* ═══ UPLOAD MODAL ═══ */}
      {showUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(440px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Upload de Documento</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 20 }}>{uploadTarget?.tipoDoc} → Firebase Storage</p>
            <label
              style={{ border: "2px dashed rgba(34,211,238,0.3)", borderRadius: 14, padding: 40, textAlign: "center", cursor: "pointer", background: uploadFiles.length > 0 ? "rgba(34,197,94,0.05)" : "rgba(34,211,238,0.03)", transition: "all 0.2s", display: "block" }}
            >
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: "none" }} onChange={selecionarArquivos} />
              {uploadFiles.length > 0 ? (
                <div><div style={{ fontSize: 32, marginBottom: 8 }}>✓</div><div style={{ fontSize: 14, fontWeight: 700, color: "#22c55e" }}>{uploadFiles.map(f => f.name).join(", ")}</div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>{(uploadFiles.reduce((s, f) => s + f.size, 0) / 1024).toFixed(0)} KB · Pronto para enviar</div></div>
              ) : (
                <div><div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>↑</div><div style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.5)" }}>Clique para selecionar arquivo</div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>PDF, JPG ou PNG · Máx 10MB</div></div>
              )}
            </label>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 12, fontFamily: "'JetBrains Mono'" }}>
              Destino: gs://lexdriver-491016.firebasestorage.app/processos/{uploadTarget?.processoId}/
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => { setShowUpload(false); setUploadFiles([]); }}>Cancelar</Btn>
              <Btn onClick={confirmUpload} disabled={uploadFiles.length === 0}>Enviar para Cloud</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ NOVO PROCESSO MODAL ═══ */}
      {showNewProcesso && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(540px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Novo Processo</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 24 }}>Cadastrar novo recurso de trânsito</p>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Nº AIT / Processo" type="text" placeholder="Ex: C42029823" value={novoProc.ait} onChange={e => setNovoProc(p => ({ ...p, ait: e.target.value }))} />
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: 6 }}>Tipo</label>
                  <select value={novoProc.tipo} onChange={e => setNovoProc(p => ({ ...p, tipo: e.target.value }))} style={{ width: "100%", padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14 }}>
                    <option>LS</option><option>CASSAÇÃO</option><option>SUSPENSÃO</option>
                  </select>
                </div>
              </div>
              <Input label="Nome do Cliente" type="text" placeholder="Nome completo" value={novoProc.cliente} onChange={e => setNovoProc(p => ({ ...p, cliente: e.target.value }))} />
              <Input label="Telefone (WhatsApp)" type="tel" placeholder="(21) 99999-9999" value={novoProc.telefone} onChange={e => setNovoProc(p => ({ ...p, telefone: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Data da Infração" type="date" value={novoProc.dtInfracao} onChange={e => setNovoProc(p => ({ ...p, dtInfracao: e.target.value }))} />
                <Input label="Data do Recurso" type="date" value={novoProc.dtRecurso} onChange={e => setNovoProc(p => ({ ...p, dtRecurso: e.target.value }))} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Honorário (R$)" type="number" placeholder="1500" value={novoProc.honorario} onChange={e => setNovoProc(p => ({ ...p, honorario: e.target.value }))} />
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: 6 }}>Forma de Pagamento</label>
                  <select value={novoProc.formaPgto} onChange={e => setNovoProc(p => ({ ...p, formaPgto: e.target.value }))} style={{ width: "100%", padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14 }}>
                    <option>A VISTA</option><option>PARCELAMENTO (2X)</option><option>PARCELAMENTO (3X)</option><option>PARCELAMENTO (4X)</option><option>PARCELAMENTO (6X)</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: 6 }}>Status</label>
                <select value={novoProc.status} onChange={e => setNovoProc(p => ({ ...p, status: e.target.value }))} style={{ width: "100%", padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14 }}>
                  <option>DEFESA PRÉVIA</option><option>RECURSO JARI</option><option>RECURSO CETRAN</option>
                </select>
              </div>

              {/* Upload opcional de documentos */}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)", marginBottom: 10 }}>DOCUMENTOS (opcional)</div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                  <label style={{ padding: "12px", borderRadius: 10, background: novoProcCnh ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${novoProcCnh ? "#22c55e33" : "rgba(255,255,255,0.08)"}`, cursor: "pointer", textAlign: "center", display: "block" }}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => selecionarArquivo(e, setNovoProcCnh)} />
                    <div style={{ fontSize: 16, marginBottom: 4 }}>{novoProcCnh ? "✓" : "↑"}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: novoProcCnh ? "#22c55e" : "rgba(255,255,255,0.5)" }}>CNH</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{novoProcCnh ? novoProcCnh.name : "Clique pra enviar"}</div>
                  </label>
                  <label style={{ padding: "12px", borderRadius: 10, background: novoProcRecibo ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${novoProcRecibo ? "#22c55e33" : "rgba(255,255,255,0.08)"}`, cursor: "pointer", textAlign: "center", display: "block" }}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => selecionarArquivo(e, setNovoProcRecibo)} />
                    <div style={{ fontSize: 16, marginBottom: 4 }}>{novoProcRecibo ? "✓" : "↑"}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: novoProcRecibo ? "#22c55e" : "rgba(255,255,255,0.5)" }}>Recibo {novoProc.status === "RECURSO JARI" ? "JARI" : novoProc.status === "RECURSO CETRAN" ? "CETRAN" : "Defesa Prévia"}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{novoProcRecibo ? novoProcRecibo.name : "Clique pra enviar"}</div>
                  </label>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => { setShowNewProcesso(false); setNovoProcCnh(null); setNovoProcRecibo(null); }}>Cancelar</Btn>
              <Btn onClick={addProcesso} disabled={!novoProc.ait || !novoProc.cliente}>Cadastrar Processo</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ NOVA ENTRADA/SAÍDA MODAL ═══ */}
      {showNewFinanceiro && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(440px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Nova Movimentação</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 24 }}>Registrar entrada ou saída no fluxo de caixa</p>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 6 }}>
                <button onClick={() => setNovaEntrada(p => ({ ...p, tipo: "entrada" }))} style={{ padding: "14px", borderRadius: 10, border: `2px solid ${novaEntrada.tipo === "entrada" ? "#22c55e" : "rgba(255,255,255,0.08)"}`, background: novaEntrada.tipo === "entrada" ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.02)", cursor: "pointer", fontFamily: "inherit", color: novaEntrada.tipo === "entrada" ? "#22c55e" : "rgba(255,255,255,0.4)", textAlign: "center" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>↑</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Entrada</div>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>Honorários, recebimentos</div>
                </button>
                <button onClick={() => setNovaEntrada(p => ({ ...p, tipo: "saida" }))} style={{ padding: "14px", borderRadius: 10, border: `2px solid ${novaEntrada.tipo === "saida" ? "#ef4444" : "rgba(255,255,255,0.08)"}`, background: novaEntrada.tipo === "saida" ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.02)", cursor: "pointer", fontFamily: "inherit", color: novaEntrada.tipo === "saida" ? "#ef4444" : "rgba(255,255,255,0.4)", textAlign: "center" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>↓</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Saída</div>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>Despesas, custos</div>
                </button>
              </div>
              <Input label="Descrição" type="text" placeholder="Ex: Honorário processo C42029823" value={novaEntrada.descricao} onChange={e => setNovaEntrada(p => ({ ...p, descricao: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Valor (R$)" type="number" placeholder="0.00" value={novaEntrada.valor} onChange={e => setNovaEntrada(p => ({ ...p, valor: e.target.value }))} />
                <Input label="Data" type="date" value={novaEntrada.data} onChange={e => setNovaEntrada(p => ({ ...p, data: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowNewFinanceiro(false)}>Cancelar</Btn>
              <Btn onClick={addFinanceiro} disabled={!novaEntrada.valor}>Registrar</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ STATUS CHANGE + UPLOAD MODAL ═══ */}
      {showStatusUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(440px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Alterar Status para: {pendingStatus}</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 20 }}>
              {["DEFESA PRÉVIA", "RECURSO JARI", "RECURSO CETRAN"].includes(pendingStatus)
                ? `Deseja anexar o recibo/protocolo da ${pendingStatus}? (opcional)`
                : "Confirma a alteração de status?"}
            </p>
            {["DEFESA PRÉVIA", "RECURSO JARI", "RECURSO CETRAN"].includes(pendingStatus) && (
              <label style={{ border: "2px dashed rgba(34,211,238,0.3)", borderRadius: 14, padding: 30, textAlign: "center", cursor: "pointer", background: statusUploadFile ? "rgba(34,197,94,0.05)" : "rgba(34,211,238,0.03)", display: "block", marginBottom: 16 }}>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => selecionarArquivo(e, setStatusUploadFile)} />
                {statusUploadFile ? (
                  <div><div style={{ fontSize: 24, marginBottom: 6 }}>✓</div><div style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>{statusUploadFile.name}</div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{(statusUploadFile.size / 1024).toFixed(0)} KB</div></div>
                ) : (
                  <div><div style={{ fontSize: 24, marginBottom: 6, opacity: 0.3 }}>↑</div><div style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)" }}>Anexar recibo (opcional)</div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>PDF, JPG ou PNG</div></div>
                )}
              </label>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => { setShowStatusUpload(false); setPendingStatus(""); setStatusUploadFile(null); }}>Cancelar</Btn>
              <Btn onClick={confirmStatusChange} disabled={importando}>{importando ? "Salvando..." : "Confirmar"}</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ RETIRADA DE SÓCIO MODAL ═══ */}
      {showRetirada && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(460px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>💸 Retirada de Sócio</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 24 }}>Registra uma saída de caixa (pró-labore / distribuição)</p>
            <div style={{ display: "grid", gap: 14 }}>
              <Input label="Sócio" type="text" placeholder="Nome do sócio" value={retirada.socio} onChange={e => setRetirada(r => ({ ...r, socio: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Valor (R$)" type="number" placeholder="0.00" value={retirada.valor} onChange={e => setRetirada(r => ({ ...r, valor: e.target.value }))} />
                <Input label="Data" type="date" value={retirada.data} onChange={e => setRetirada(r => ({ ...r, data: e.target.value }))} />
              </div>
              <Input label="Observação (opcional)" type="text" placeholder="Ex: distribuição de lucros março" value={retirada.obs} onChange={e => setRetirada(r => ({ ...r, obs: e.target.value }))} />
              <label style={{ padding: "12px", borderRadius: 10, background: retiradaComprovante ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${retiradaComprovante ? "#22c55e33" : "rgba(255,255,255,0.08)"}`, cursor: "pointer", textAlign: "center", display: "block" }}>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => selecionarArquivo(e, setRetiradaComprovante)} />
                <div style={{ fontSize: 16, marginBottom: 4 }}>{retiradaComprovante ? "✓" : "↑"}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: retiradaComprovante ? "#22c55e" : "rgba(255,255,255,0.5)" }}>Comprovante (opcional)</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{retiradaComprovante ? retiradaComprovante.name : "Clique pra anexar"}</div>
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => { setShowRetirada(false); setRetiradaComprovante(null); }}>Cancelar</Btn>
              <Btn onClick={addRetiradaSocio} disabled={importando || !retirada.valor || !retirada.socio}>{importando ? "Registrando..." : "Registrar Retirada"}</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ EDITAR PROCESSO MODAL ═══ */}
      {showEditProcesso && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, animation: "fadeIn 0.2s", padding: 16 }} onClick={e => { if (e.target === e.currentTarget) fecharModais(); }}>
          <Card modal style={{ width: "min(540px, 92vw)", animation: "fadeIn 0.3s", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Editar Processo</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 24 }}>Altere os dados cadastrais do recurso</p>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Nº AIT / Processo" type="text" value={showEditProcesso.ait || ""} onChange={e => setShowEditProcesso(p => ({ ...p, ait: e.target.value }))} />
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: 6 }}>Tipo</label>
                  <select value={showEditProcesso.tipo} onChange={e => setShowEditProcesso(p => ({ ...p, tipo: e.target.value }))} style={{ width: "100%", padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14 }}>
                    <option>LS</option><option>CASSAÇÃO</option><option>SUSPENSÃO</option>
                  </select>
                </div>
              </div>
              <Input label="Nome do Cliente" type="text" value={showEditProcesso.cliente || ""} onChange={e => setShowEditProcesso(p => ({ ...p, cliente: e.target.value }))} />
              <Input label="Telefone (WhatsApp)" type="tel" value={showEditProcesso.telefone || ""} onChange={e => setShowEditProcesso(p => ({ ...p, telefone: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                <Input label="Data da Infração" type="date" value={showEditProcesso.dtInfracao || ""} onChange={e => setShowEditProcesso(p => ({ ...p, dtInfracao: e.target.value }))} />
                <Input label="Data do Recurso" type="date" value={showEditProcesso.dtRecurso || ""} onChange={e => setShowEditProcesso(p => ({ ...p, dtRecurso: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: 6 }}>Forma de Pagamento</label>
                <select value={showEditProcesso.formaPgto} onChange={e => setShowEditProcesso(p => ({ ...p, formaPgto: e.target.value }))} style={{ width: "100%", padding: "11px 16px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 14 }}>
                  <option>A VISTA</option><option>PARCELAMENTO (2X)</option><option>PARCELAMENTO (3X)</option><option>PARCELAMENTO (4X)</option><option>PARCELAMENTO (6X)</option><option>PARCELAMENTO (10X)</option>
                </select>
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", padding: "8px 12px", borderRadius: 8, background: "rgba(34,211,238,0.04)", border: "1px solid rgba(34,211,238,0.1)" }}>
                Honorário e saldo são editados no painel 💰 Financeiro do processo. Status é alterado pelo seletor de status.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowEditProcesso(null)}>Cancelar</Btn>
              <Btn onClick={salvarEdicaoProcesso} disabled={!showEditProcesso.ait || !showEditProcesso.cliente}>Salvar</Btn>
            </div>
          </Card>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ROOT — Auth Gate
// ══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = doc(usersRef, firebaseUser.uid);
          const snap = await getDoc(userDoc);
          if (snap.exists()) {
            setUser({ uid: firebaseUser.uid, email: firebaseUser.email, ...snap.data() });
          } else {
            // Sem perfil no banco = acesso NÃO liberado. Nunca assumir admin.
            setUser({ uid: firebaseUser.uid, email: firebaseUser.email, nome: firebaseUser.displayName || firebaseUser.email.split("@")[0], role: "pendente" });
          }
        } catch {
          // Erro ao ler o perfil: trata como pendente (fail-closed), não admin.
          setUser({ uid: firebaseUser.uid, email: firebaseUser.email, nome: firebaseUser.displayName || firebaseUser.email.split("@")[0], role: "pendente" });
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: "3px solid rgba(34,211,238,0.2)", borderTopColor: "#22d3ee", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Carregando...</div>
        </div>
      </div>
    );
  }

  if (user && user.role === "pendente") {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap');`}</style>
        <div style={{ textAlign: "center", maxWidth: 440, padding: 40 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>🔒</div>
          <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Aguardando Aprovação</h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
            Sua conta <span style={{ color: "#22d3ee" }}>{user.email}</span> foi criada com sucesso.
          </p>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, lineHeight: 1.6, marginBottom: 32 }}>
            Um administrador precisa liberar seu acesso. Você será notificado quando sua conta for aprovada.
          </p>
          <button onClick={handleLogout} style={{ padding: "10px 28px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            Sair
          </button>
        </div>
      </div>
    );
  }

  return user ? <MainApp user={user} onLogout={handleLogout} /> : <LoginScreen onLogin={setUser} />;
}
